import React, { useEffect, useMemo, useState } from "react";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BadgeCheck,
  Building2,
  Calendar,
  CheckCircle2,
  ClipboardCopy,
  CreditCard,
  FileCheck2,
  FileText,
  Hash,
  Landmark,
  Link as LinkIcon,
  Mail,
  MapPin,
  Phone,
  ShieldCheck,
  User,
  XCircle,
  ExternalLink,
  Shield,
  Wallet,
} from "lucide-react";
import CompanyTabs from "../../Component/AdminComponents/CompanyTabs";

import AgentsView from "../Main/Admin/AgentsView";
import InvoicesView from "../Main/Retailer/InvoicesView";
import CompanySales from "./RetailerDash/CompanySales";
import AssignCompanyProducts from "./AssignProduct/AssignCompanyProducts";
import CompanyUsers from "./CompanyUsers";
import axios from "axios";
import { APPID, BASE_URL, SecretKey, SWAGGER_URL } from "../../config/api";
import toast from "react-hot-toast";
import RemarkChat from "./RemarkChat";

const steps = [
  { id: 1, name: "Verify Email & Phone" },
  { id: 2, name: "Payment" },
  { id: 3, name: "PAN Verification" },
  { id: 4, name: "GST Details" },
  { id: 5, name: "Bank Details" },
  { id: 6, name: "Agreement" },
  { id: 7, name: "E-Sign Verify" },
];

const tone = {
  blue: "bg-blue-50 text-blue-700 border-blue-200",
  green: "bg-emerald-50 text-emerald-700 border-emerald-200",
  red: "bg-rose-50 text-rose-700 border-rose-200",
  gray: "bg-slate-50 text-slate-700 border-slate-200",
  amber: "bg-amber-50 text-amber-700 border-amber-200",
};

function safeJsonParse(value) {
  try {
    if (!value) return null;
    if (typeof value === "object") return value;
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function fmt(value) {
  if (value === null || value === undefined || value === "") return "—";
  return String(value);
}

function toLocal(dt) {
  if (!dt) return "—";
  const d = new Date(dt);
  return Number.isNaN(d.getTime()) ? String(dt) : d.toLocaleString();
}

function Badge({ kind = "blue", children }) {
  return (
    <span className={`inline-flex items-center rounded-full border px-2 py-1 text-xs font-semibold ${tone[kind]}`}>
      {children}
    </span>
  );
}

function Pill({ ok, yes = "Yes", no = "No" }) {
  return ok ? (
    <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700">
      <CheckCircle2 className="h-3.5 w-3.5" />
      {yes}
    </span>
  ) : (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-1 text-xs font-bold text-slate-700">
      <XCircle className="h-3.5 w-3.5" />
      {no}
    </span>
  );
}

function CopyBtn({ value }) {
  const [copied, setCopied] = React.useState(false);

  if (!value || value === "—") return null;

  const handleCopy = async () => {
    await navigator.clipboard.writeText(String(value));
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={handleCopy}
        className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-2.5 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100 transition"
      >
        {/* Icon changes when copied */}
        {copied ? (
          <CheckCircle2 className="h-4 w-4 text-emerald-600" />
        ) : (
          <ClipboardCopy className="h-4 w-4" />
        )}

        {/* Hide label on small screens */}
        <span className="hidden sm:inline">
          {copied ? "Copied" : "Copy"}
        </span>
      </button>

      {/* Tooltip */}
      {copied && (
        <span className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-slate-900 px-2 py-1 text-[10px] font-semibold text-white shadow">
          Copied ✓
        </span>
      )}
    </div>
  );
}


function InfoCard({ icon: Icon, label, value, copy = false, right, isRetailerMobile }) {
  const v = fmt(value);
  return (
    <div className={`flex flex-row items-start sm:items-center gap-3 rounded-2xl p-4 transition-all duration-300
      ${
        isRetailerMobile
          ? "bg-white/10 backdrop-blur-md border border-white/20"
          : "border border-blue-100 bg-white shadow-sm"
      }`}>
      <div className="mt-0.5 rounded-xl bg-blue-50 p-2 text-blue-700">
        <Icon className="h-4 w-4" />
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-xs font-semibold text-slate-500">{label}</p>
        <p className="mt-0.5 break-words text-sm font-semibold text-slate-900">{v}</p>
      </div>
      <div className="flex items-center gap-2">
        {right}
        {copy && v !== "—" ? <CopyBtn value={v} /> : null}
      </div>
    </div>
  );
}

function Section({ title, subtitle, right, children, isRetailerMobile }) {
  return (
    <div className={`w-full rounded-2xl p-4 md:p-5 transition-all duration-300
  ${
    isRetailerMobile
      ? "bg-white/10 backdrop-blur-xl border border-white/30 shadow-[0_20px_50px_rgba(31,38,135,0.25)]"
      : "border border-blue-100 bg-white shadow-sm"
  }`}>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          {subtitle ? <p className="mt-1 text-sm font-semibold text-slate-600">{subtitle}</p> : null}
        </div>
        {right}
      </div>
      {children}
    </div>
  );
}

// Step completion rules
function getStepStates(normalized) {
  const emailPhoneDone = Boolean(normalized?.is_mail_verified) && Boolean(normalized?.is_wa_verified);
  const paymentDone = Number(normalized?.is_payment_success) === 1;
  const panDone = Number(normalized?.pan_verified) === 1;
  const gstDone = Boolean(normalized?.gst);
  const bankDone = Boolean(normalized?.account_no) && Boolean(normalized?.bank_name) && Boolean(normalized?.branch_name);
  const agreementDone = Number(normalized?.is_verified) >= 6; // replace if you have explicit flag
  const esignDone = Number(normalized?.esign_verified) === 1;

  return { 1: emailPhoneDone, 2: paymentDone, 3: panDone, 4: gstDone, 5: bankDone, 6: agreementDone, 7: esignDone };
}

function Stepper({ currentStage, states, stepsOverride }) {
  const stepsToRender = stepsOverride || steps;
  return (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
    
      {stepsToRender.map((s) => {
        const done = Boolean(states?.[s.id]);
        const active = Number(currentStage) === s.id;

        return (
          <div
            key={s.id}
            className={[
              "flex items-center justify-between rounded-2xl border p-4",
              done ? "border-emerald-200 bg-emerald-50" : active ? "border-blue-200 bg-blue-50" : "border-blue-100 bg-white",
            ].join(" ")}
          >
            <div className="flex items-center gap-3">
              <div
                className={[
                  "flex h-10 w-10 items-center justify-center rounded-xl border",
                  done
                    ? "border-emerald-200 bg-white text-emerald-700"
                    : active
                      ? "border-blue-200 bg-white text-blue-700"
                      : "border-blue-100 bg-blue-50 text-blue-700",
                ].join(" ")}
              >
                {done ? <CheckCircle2 className="h-5 w-5" /> : <span className="text-sm font-semibold">{s.id}</span>}
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-900">{s.name}</p>
                <p className="text-xs font-semibold text-slate-500">{done ? "Completed" : active ? "In progress" : "Pending"}</p>
              </div>
            </div>

            {done ? <Badge kind="green">Done</Badge> : active ? <Badge kind="blue">Active</Badge> : <Badge kind="gray">Pending</Badge>}
          </div>
        );
      })}
    </div>
  );
}

function LinkBtn({ href, children }) {
  if (!href) return null;
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
    >
      <ExternalLink className="h-4 w-4" />
      {children}
    </a>
  );
}

export default function UserDetails() {
  
  const navigate = useNavigate();
  const { id } = useParams();
  console.log("id from promoter: ",id)
  const location = useLocation();
  const { entityType, data } = location.state || {};
  const [saleAnalytics, setSaleAnalytics] = React.useState(null);
  const [balanceAmount, setBalanceAmount] = useState(0);
  const [loadingAnalytics, setLoadingAnalytics] = React.useState(false);
  const [showPaymentModal, setShowPaymentModal] = React.useState(false);
  const [updatingPayment, setUpdatingPayment] = React.useState(false);
  // const [selectedPayment, setSelectedPayment] = React.useState(null);
  const [currentTab, setCurrentTab] = React.useState(0);

  const [showLogoutConfirm, setShowLogoutConfirm] = React.useState(false);
  const [forcingLogout, setForcingLogout] = React.useState(false);

  const [agreementFile, setAgreementFile] = useState(null);
  const [uploadingAgreement, setUploadingAgreement] = useState(false);
  const [agreementUploaded, setAgreementUploaded] = useState(false);

  const [esignFile, setEsignFile] = useState(null);
  const [uploadingEsign, setUploadingEsign] = useState(false);
  const [documentId, setDocumentId] = useState(null);


  const [aadhaarLast4, setAadhaarLast4] = useState(["", "", "", ""]);
  const [aadhaarError, setAadhaarError] = useState("");
  const aadhaarInputsRef = React.useRef([]);
  const [aadhaarStatus, setAadhaarStatus] = useState("idle");
  const [esignStatusLoading, setEsignStatusLoading] = useState(false);
  const [aadhaarVerified, setAadhaarVerified] = useState(false);

  const [showWalletConfirm, setShowWalletConfirm] = useState(false);
  const [updatingWallet, setUpdatingWallet] = useState(false);


  const [showUserPaymentModal, setShowUserPaymentModal] = useState(false);


   const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
    useEffect(() => {
      const handleResize = () => setIsMobile(window.innerWidth < 768);
      window.addEventListener("resize", handleResize);
      return () => window.removeEventListener("resize", handleResize);
    }, []);
    
    const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");
    
    const isRetailerMobile =
    typeof window !== "undefined" &&
    window.innerWidth < 640 &&
    (Number(authUser?.role) === 5 || Number(authUser?.role) === 6);


  const normalized = useMemo(() => {
  if (!entityType || !data) return null;

  if (entityType === "company") {
    return {
      ...data, // ✅ include ALL company fields
      id: data.id,
      name: data.business_name,
      contactPerson: data.contact_person,
      phone: data.contact_phone,
      email: data.contact_email,
      role: "MCP",
    };
  }

  if (entityType === "agent") {
    return {
      ...data, // ✅ include ALL agent fields
      id: data.id,
      name: data.business_name || data.contact_person,
      contactPerson: data.contact_person,
      phone: data.contact_phone,
      email: data.contact_email,
      role: "CP",
    };
  }

  if (entityType === "retailer") {
    return {
      ...data,
      id: data.id,
      business_name: data.name || data.business_name,
      contact_person: data.contactPerson || data.contact_person,
      contact_phone: data.mobile || data.contact_phone,
      contact_email: data.email || data.contact_email,
      role: "ARP",
    };
  }

   if (entityType === "retailer_user") {
    return {
      ...data,
      id: data.id,
      business_name: data.name || data.business_name,
      contact_person: data.contactPerson || data.contact_person,
      contact_phone: data.mobile || data.contact_phone,
      contact_email: data.email || data.contact_email,
      role: "Promoter",
      rawrole:data.role,
    };
  }

  return null;
}, [entityType, data]);


const [userPaymentSelection, setUserPaymentSelection] = useState({
  paynow: Number(normalized?.pay_now) === 1,
  paylater: Number(normalized?.pay_later) === 1,
});

useEffect(() => {
  if (showUserPaymentModal && normalized) {
    setUserPaymentSelection({
      paynow: Number(normalized.pay_now) === 1,
      paylater: Number(normalized.pay_later) === 1,
    });
  }
}, [showUserPaymentModal, normalized]);

const handleWalletToggle = async () => {
  const nextValue = Number(normalized.allow_wallet) === 1 ? 0 : 1;

  try {
    setUpdatingWallet(true);

    const res = await axios.post(`${BASE_URL}common/update`, {
      id: normalized.id,
      allow_wallet: nextValue,
      contact_email: normalized.contact_email,
      pincode: normalized.pincode,
      role: normalized.rawrole,
    });

    if (res.data?.status || res.data?.success) {
      toast.success(
        nextValue === 1
          ? "Wallet access enabled"
          : "Wallet access disabled"
      );

      // ✅ instant UI update
      normalized.allow_wallet = nextValue;

      setShowWalletConfirm(false);
    } else {
      toast.error("Failed to update wallet permission");
    }
  } catch (err) {
    console.error(err);
    toast.error("Something went wrong");
  } finally {
    setUpdatingWallet(false);
  }
};


useEffect(() => {
  if (!id || entityType !== "retailer") return;

  async function fetchSaleAnalytics() {
    try {
      setLoadingAnalytics(true);

      const res = await axios.get(
        `${BASE_URL}warranty/sale-records?retailer_id=${id}`
      );

      if (res.data?.success) {
        setSaleAnalytics(res.data.analytics);
      }
    } catch (error) {
      console.error("Failed to fetch sale analytics", error);
    } finally {
      setLoadingAnalytics(false);
    }
  }

  fetchSaleAnalytics();
}, [id, entityType]);

  const panData = useMemo(() => safeJsonParse(normalized?.pan_json), [normalized?.pan_json]);
  const esignData = useMemo(() => safeJsonParse(normalized?.esign_json), [normalized?.esign_json]);

  // const stepStates = useMemo(() => getStepStates(normalized), [normalized]);

  const stepStates = useMemo(() => {
    if (entityType === "retailer_user") {
      return {
        1:
          // Number(normalized?.is_verified) === 1,
          Number(normalized?.is_mail_verified) === 1 &&
          Number(normalized?.is_wa_verified) === 1,

        5:
          Number(normalized?.is_verified) === 7 &&
          Number(normalized?.bank_verified) === 1 &&
          normalized?.bank_json !== null,
      };
    }

    return getStepStates(normalized);
  }, [normalized, entityType]);

  // Signed document link support (if backend provides later)
  // ✅ If you have a real signed doc URL, add it in payload as e.g. `esign_signed_doc_url`
  const signedDocUrl =
    normalized?.esign_signed_doc_url ||
    esignData?.signed_document_url ||
    esignData?.signed_doc_url ||
    null;

  // Signing link (your payload includes this)
  const signingLink = esignData?.signing_link || null;

  const mailOk = Boolean(normalized?.is_mail_verified);
  const waOk = Boolean(normalized?.is_wa_verified);

  const gstType = normalized?.business_type ? String(normalized.business_type).toUpperCase() : "—";
  const entityType1 =
    panData?.type || normalized?.company_type || normalized?.entity_type || "—"; // add company_type in payload if you have it


  useEffect(() => {
    setCurrentTab(0); // always go to Overview
  }, [id]);

  const [paymentSelection, setPaymentSelection] = React.useState({
    paynow: Number(normalized?.admin_paynow_status) === 1,
    paylater: Number(normalized?.admin_paylater_status) === 1,
  });


  useEffect(() => {
    if (showPaymentModal && normalized) {
      setPaymentSelection({
        paynow: Number(normalized.admin_paynow_status) === 1,
        paylater: Number(normalized.admin_paylater_status) === 1,
      });
    }
  }, [showPaymentModal, normalized]);



  const handleAdminPaymentUpdate = async (selection) => {
    const payload = {
      admin_paynow_status: selection.paynow ? 1 : 0,
      admin_paylater_status: selection.paylater ? 1 : 0,
      admin_status_change_date: new Date().toISOString().split("T")[0],
    };

    try {
      setUpdatingPayment(true);

      const res = await fetch(
        `${BASE_URL}company/update-fields/${normalized.id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );

      const json = await res.json();

      if (json.status) {
        toast.success("Payment override updated successfully");

        // ✅ update UI immediately (NO reload)
        setShowPaymentModal(false);

        // IMPORTANT: update normalized values locally
        normalized.admin_paynow_status = payload.admin_paynow_status;
        normalized.admin_paylater_status = payload.admin_paylater_status;
      } else {
        toast.error("Failed to update payment override");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong while updating payment");
    } finally {
      setUpdatingPayment(false);
    }
  };



  const handleUserPaymentUpdate = async (selection) => {
  try {
    setUpdatingPayment(true);

    const authUser = JSON.parse(localStorage.getItem("authUserData") || "{}");

    const canGivePayLater = Number(authUser?.pay_later) === 1;
    const canGivePayNow = Number(authUser?.pay_now) === 1;

    // 🚫 permission check
    if (selection.paylater && !canGivePayLater) {
      toast.error("You don't have permission for Pay Later");
      return;
    }

    if (selection.paynow && !canGivePayNow) {
      toast.error("You don't have permission for Pay Now");
      return;
    }

    const payload = {
      id: normalized.id,
      contact_email: normalized.contact_email,
      pincode: normalized.pincode,
      role: normalized.rawrole,

      pay_now: selection.paynow ? 1 : 0,
      pay_later: selection.paylater ? 1 : 0,
    };

    const res = await fetch(`${BASE_URL}common/update`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(payload),
    });

    const json = await res.json();

    if (json.status) {
      toast.success("User payment preference updated");

      setShowUserPaymentModal(false);

      // ✅ update UI
      normalized.pay_now = payload.pay_now;
      normalized.pay_later = payload.pay_later;

    } else {
      toast.error("Failed to update payment preference");
    }
  } catch (err) {
    console.error(err);
    toast.error("Something went wrong");
  } finally {
    setUpdatingPayment(false);
  }
};


  const handleToggleLogout = async () => {
    const nextLogoutState = Number(normalized.is_logout) === 1 ? 0 : 1;

    try {
      setForcingLogout(true);

      const res = await axios.post(`${BASE_URL}common/logout`, {
        id: normalized.id,
        is_logout: nextLogoutState,
      });

      if (res.data?.status || res.data?.success) {
        toast.success(
          nextLogoutState === 1
            ? "User force-logged out successfully"
            : "Force logout removed successfully"
        );

        // 🔁 update UI instantly
        normalized.is_logout = nextLogoutState;
        setShowLogoutConfirm(false);
      } else {
        toast.error("Failed to update logout status");
      }
    } catch (err) {
      console.error(err);
      toast.error("Something went wrong while updating logout status");
    } finally {
      setForcingLogout(false);
    }
  };

  const handleAgreementUpload = async () => {
  if (!esignFile) {
    toast.error("Please upload a document");
    return;
  }

  try {
    setUploadingEsign(true);

    const formData = new FormData();
    formData.append("document", esignFile);

    const uploadRes = await fetch(
      `${SWAGGER_URL}/Cashfree/upload-esign-document`,
      {
        method: "POST",
        headers: {
          AppId: APPID,
          SecretKey: SecretKey,
        },
        body: formData,
      }
    );

    const uploadData = await uploadRes.json();

    if (uploadData.status !== "SUCCESS") {
      throw new Error("Upload failed");
    }

    // If API returns doc id like: { status:"SUCCESS", documentId: 123 }
    const docId = uploadData.documentId || uploadData;

    setDocumentId(docId);
    setAgreementUploaded(true);

    toast.success("Document uploaded successfully");

  } catch (err) {
    console.error(err);
    toast.error(err.message || "Upload failed");
  } finally {
    setUploadingEsign(false);
  }
};


  const handleAadhaarBoxChange = (index, value) => {
  if (!/^\d?$/.test(value)) return;

  const updated = [...aadhaarLast4];
  updated[index] = value;
  setAadhaarLast4(updated);
  setAadhaarError("");

  // Move to next box
  if (value && index < 3) {
    aadhaarInputsRef.current[index + 1]?.focus();
  }
};

const handleAadhaarKeyDown = (index, e) => {
  if (e.key === "Backspace" && !aadhaarLast4[index] && index > 0) {
    aadhaarInputsRef.current[index - 1]?.focus();
  }
};

  const verifyAadhaar = async () => {
    if (aadhaarLast4.join("").length !== 4) {
      toast.error("Enter valid last 4 digits");
      return;
    }

    const openCenteredPopup = (url, width = 600, height = 600) => {
      const left = (window.innerWidth - width) / 2;
      const top = (window.innerHeight - height) / 2;
      window.open(
        url,
        "_blank",
        `width=${width},height=${height},top=${top},left=${left}`
      );
    };

    try {
      setAadhaarStatus("loading");

      // 🔵 Initiate eSign
      const res = await fetch(
        `${BASE_URL}upload-for-esign/${normalized.role_id || 5}/${normalized.id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            aadhaar_last_four_digit: aadhaarLast4.join(""),
          }),
        }
      );

      const data = await res.json();

      if (data.status && data.esign_response) {
        openCenteredPopup(data.esign_response.signing_link);
        setAadhaarStatus("verifying");
        return;
      }

      toast.error(data.message || "Failed to initiate eSign");
      setAadhaarStatus("error");

    } catch (err) {
      console.error(err);
      toast.error("Something went wrong");
      setAadhaarStatus("error");
    }
  };

  useEffect(() => {
  if (Number(normalized?.esign_verified) === 1) {
    setAadhaarVerified(true);
  }
}, [normalized?.esign_verified]);

const aadhaarValid = aadhaarLast4.join("").length === 4;


  if (!entityType || !data || !normalized) {
    return (
      <div className="mx-auto max-w-5xl p-4 md:p-8">
        <div className="rounded-2xl border border-blue-100 bg-white p-6 text-center shadow-sm">
          <p className="text-base font-semibold text-slate-900">No user data found</p>
          <p className="mt-1 text-sm font-semibold text-slate-600">
            Refresh clears route state. Go back and open details again (Company ID: <span className="font-bold">{id}</span>).
          </p>
          <button
            type="button"
            onClick={() => navigate(-1)}
            className="mt-4 inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
          >
            <ArrowLeft className="h-4 w-4" />
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
   <div
  className={`mx-auto p-2 md:p-8 min-h-screen transition-all duration-300
    ${
      isRetailerMobile
        ? "bg-gradient-to-br from-slate-300 via-blue-200 to-slate-500"
        : ""
    }`}
>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            
            <h1 className="text-lg font-semibold text-slate-900 md:text-2xl break-words">
              {fmt(normalized.business_name)} ({fmt(normalized.company_code)})
            </h1>
          
          </div>
          {authUser?.role !== 5 && (
              <>

              <div className="flex gap-1">
                <Badge kind={Number(normalized.is_payment_success) === 1 ? "green" : "amber"}>
              {Number(normalized.is_payment_success) === 1 ? "Payment Success" : "Payment Pending"}
            </Badge>
            <Badge kind={Number(normalized.esign_verified) === 1 ? "green" : "gray"}>
              {Number(normalized.esign_verified) === 1 ? "E-Sign Verified" : "E-Sign Pending"}
            </Badge>
             <Badge
            kind="blue"
            className="text-2xl font-semibold px-4 py-2"
          >
            Role: {normalized.role}
          </Badge>
              </div>
            </>
            )}

          <div className="mt-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-600">
            <span>
              Trade Name: <span className="font-semibold text-slate-900">{fmt(normalized.trade_name)}</span>
            </span>
            <span className="text-slate-300">•</span>
            <span>
              Created: <span className="font-semibold text-slate-900">{toLocal(normalized.created_at)}</span>
            </span>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          {/* <button
            type="button"
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50"
          >
            <ArrowLeft className="h-4 w-4" />
            Back
          </button>

          <button
            type="button"
            onClick={() => navigator.clipboard.writeText(JSON.stringify(normalized, null, 2))}
            className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
          >
            <ClipboardCopy className="h-4 w-4" />
            Copy Payload
          </button> */}
          {entityType == "retailer_user" && (
            <div
              className={`flex items-center gap-3 rounded-xl border px-2 py-1 text-sm font-semibold transition-all
                ${
                  Number(normalized.allow_wallet) === 1
                    ? "border-emerald-200 bg-emerald-50 text-emerald-700"
                    : "border-slate-200 bg-slate-100 text-slate-500"
                }
              `}
            >
              <span className="text-sm font-semibold">
                Wallet Access
              </span>

              <button
                onClick={() => setShowWalletConfirm(true)}
                className={`relative w-12 h-6 rounded-full transition-all duration-300
                  ${
                    Number(normalized.allow_wallet) === 1
                      ? "bg-emerald-400"
                      : "bg-slate-300"
                  }
                `}
              >
                {/* Toggle Circle */}
                <span
                  className={`absolute top-1 left-1 w-4 h-4 rounded-full bg-white shadow-md transform transition-all duration-300
                    ${
                      Number(normalized.allow_wallet) === 1
                        ? "translate-x-6"
                        : "translate-x-0"
                    }
                  `}
                />
              </button>
            </div>
          )}

          <button
            onClick={() => setShowLogoutConfirm(true)}
            className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold
              ${
                Number(normalized.is_logout) === 1
                  ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
                  : "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
              }
            `}
          >
            <Shield className="h-4 w-4" />
            {Number(normalized.is_logout) === 1 ? "Remove Force Logout" : "Force Logout"}
          </button>

        </div>
      </div>

      <CompanyTabs currentTab={currentTab} setCurrentTab={setCurrentTab} entityType={entityType} isRetailerMobile={isRetailerMobile} />

      {currentTab === 1 && (
        <div className="grid grid-cols-1 gap-4 md:gap-6 lg:grid-cols-3">
        {/* Main */}
        <div className="space-y-6 lg:col-span-2">
          <Section
            isRetailerMobile={isRetailerMobile}
            title="Onboarding Progress"
            subtitle="Clear step tracker based on your payload flags"
            right={
              <div className="flex flex-wrap items-center gap-2">
                <Badge kind="blue">
                  Stage: <span className="ml-1 font-semibold">{fmt(normalized.is_verified)}</span>
                </Badge>
                <Badge kind={mailOk ? "green" : "gray"}>
                  <Mail className="mr-1 h-3.5 w-3.5" /> Email {mailOk ? "Verified" : "Pending"}
                </Badge>
                <Badge kind={waOk ? "green" : "gray"}>
                  <Phone className="mr-1 h-3.5 w-3.5" /> Phone/WA {waOk ? "Verified" : "Pending"}
                </Badge>
              </div>
            }
          >
            <Stepper
              currentStage={normalized.is_verified}
              states={stepStates}
              stepsOverride={
                entityType === "retailer_user"
                  ? [
                      { id: 1, name: "Verify Email & Phone" },
                      { id: 5, name: "Bank Details" },
                    ]
                  : null
              }
            />
          </Section>

          <Section title="Company & Contact" isRetailerMobile={isRetailerMobile}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <InfoCard icon={Building2} label="Business Name" value={normalized.business_name} copy isRetailerMobile={isRetailerMobile}/>
              <InfoCard icon={User} label="Contact Person" value={normalized.contact_person} copy isRetailerMobile={isRetailerMobile} />
              <InfoCard icon={Phone} label="Contact Phone" value={normalized.contact_phone} copy  isRetailerMobile={isRetailerMobile}/>
              <InfoCard icon={Mail} label="Contact Email" value={normalized.contact_email} copy isRetailerMobile={isRetailerMobile}/>
            </div>
          </Section>

          <Section title="Address" isRetailerMobile={isRetailerMobile}>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <InfoCard icon={MapPin} label="Address Line 1" value={normalized.address_line1} isRetailerMobile={isRetailerMobile} />
              <InfoCard icon={MapPin} label="Address Line 2" value={normalized.address_line2} isRetailerMobile={isRetailerMobile} />
              <InfoCard icon={MapPin} label="City" value={normalized.city} isRetailerMobile={isRetailerMobile} />
              <InfoCard icon={MapPin} label="District" value={normalized.district} isRetailerMobile={isRetailerMobile} />
              <InfoCard icon={MapPin} label="State" value={normalized.state} isRetailerMobile={isRetailerMobile}/>
              <InfoCard icon={Hash} label="Pincode" value={normalized.pincode} copy isRetailerMobile={isRetailerMobile}/>
            </div>
          </Section>

          {/* ✅ KYC improved */}
          {entityType !== "retailer_user" && (
          <Section
          isRetailerMobile={isRetailerMobile}
            title="KYC: PAN & GST"
            subtitle="PAN verification + GST details + business classifications"
            right={<Badge kind={stepStates[3] ? "green" : "amber"}>{stepStates[3] ? "PAN Done" : "PAN Pending"}</Badge>}
          >
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <InfoCard icon={ShieldCheck} label="PAN Number" value={normalized.pan} copy isRetailerMobile={isRetailerMobile}/>
              <InfoCard
                icon={Shield}
                label="PAN Verified"
                value={Number(normalized.pan_verified) === 1 ? "Yes" : "No"}
                right={<Pill ok={Number(normalized.pan_verified) === 1} />}
                isRetailerMobile={isRetailerMobile}
              />

              <InfoCard icon={FileText} label="GST Number" value={normalized.gst || "Not Provided"} copy={Boolean(normalized.gst)} isRetailerMobile={isRetailerMobile}/>
              <InfoCard icon={FileCheck2} label="GST Type" value={gstType} isRetailerMobile={isRetailerMobile} />

              <InfoCard icon={Building2} label="Business Entity Type" value={entityType1} isRetailerMobile={isRetailerMobile}/>
              <InfoCard icon={FileText} label="Role" value={fmt(normalized.role)} isRetailerMobile={isRetailerMobile}/>

              {/* PAN JSON extracted */}
              {panData ? (
                <div className="md:col-span-2 rounded-2xl border border-blue-100 bg-blue-50 p-2">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <p className="text-sm font-semibold text-blue-900">PAN Verification Extract</p>
                      <p className="mt-0.5 text-xs font-semibold text-blue-800/70">
                        Reference ID: <span className="font-semibold">{fmt(panData.reference_id)}</span> • Status:{" "}
                        <span className="font-semibold">{fmt(panData.status)}</span>
                      </p>
                    </div>
                    <Badge kind={String(panData.status || "").toUpperCase() === "VALID" ? "green" : "blue"}>
                      {fmt(panData.status)}
                    </Badge>
                  </div>

                  <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <InfoCard icon={BadgeCheck} label="Registered Name" value={panData.registered_name} isRetailerMobile={isRetailerMobile}/>
                    <InfoCard icon={BadgeCheck} label="PAN Card Name" value={panData.name_pan_card} isRetailerMobile={isRetailerMobile}/>
                    <InfoCard icon={User} label="Name Provided" value={panData.name_provided} isRetailerMobile={isRetailerMobile}/>
                    <InfoCard icon={Hash} label="Masked Aadhaar" value={panData.masked_aadhaar_number} copy isRetailerMobile={isRetailerMobile}/>

                    <InfoCard icon={Calendar} label="DOB" value={panData.date_of_birth} isRetailerMobile={isRetailerMobile} />
                    <InfoCard icon={User} label="Gender" value={panData.gender} isRetailerMobile={isRetailerMobile}/>
                    <InfoCard icon={Phone} label="Mobile (masked)" value={panData.mobile_number} isRetailerMobile={isRetailerMobile} />
                    <InfoCard icon={Mail} label="Email (masked)" value={panData.email} isRetailerMobile={isRetailerMobile} />

                    <InfoCard
                      icon={ShieldCheck}
                      label="Aadhaar Linked"
                      value={panData.aadhaar_linked ? "Yes" : "No"}
                      right={<Pill ok={Boolean(panData.aadhaar_linked)} />}
                      isRetailerMobile={isRetailerMobile}
                    />
                    <InfoCard icon={FileText} label="PAN Holder Type" value={panData.type} isRetailerMobile={isRetailerMobile}/>
                  </div>

                  {panData?.address?.full_address ? (
                    <div className="mt-3 rounded-2xl border border-blue-100 bg-white p-4">
                      <p className="text-xs font-semibold text-slate-500">PAN Address</p>
                      <p className="mt-1 text-sm font-semibold text-slate-900">{panData.address.full_address}</p>
                      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
                        <InfoCard icon={MapPin} label="City" value={panData.address.city} isRetailerMobile={isRetailerMobile}/>
                        <InfoCard icon={MapPin} label="State" value={panData.address.state} isRetailerMobile={isRetailerMobile}/>
                        <InfoCard icon={Hash} label="Pincode" value={panData.address.pincode} isRetailerMobile={isRetailerMobile}/>
                        <InfoCard icon={MapPin} label="Country" value={panData.address.country} isRetailerMobile={isRetailerMobile} />
                      </div>
                    </div>
                  ) : null}

                  <details className="mt-3 rounded-2xl border border-blue-100 bg-white p-4">
                    <summary className="cursor-pointer text-sm font-semibold text-blue-700">
                      View Raw PAN JSON
                    </summary>
                    <pre className="mt-3 max-h-96 overflow-auto rounded-xl bg-slate-900 p-4 text-xs text-slate-100">
                      {JSON.stringify(panData, null, 2)}
                    </pre>
                  </details>
                </div>
              ) : (
                <div className="md:col-span-2 rounded-2xl border border-blue-100 bg-white p-4">
                  <p className="text-sm font-bold text-slate-900">PAN JSON not available</p>
                  <p className="mt-1 text-xs font-semibold text-slate-500">Field pan_json is empty or invalid JSON.</p>
                </div>
              )}
            </div>
          </Section>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Section title="Account Details" isRetailerMobile={isRetailerMobile}>
            <div className="grid grid-cols-1 gap-3">
              <InfoCard icon={Building2} label="Zoho ID" value={normalized.zoho_id || "—"} copy={Boolean(normalized.zoho_id)} isRetailerMobile={isRetailerMobile}/>
              <InfoCard icon={Building2} label="Zoho Org ID" value={normalized.zoho_org_id || "Not Connected"} copy={Boolean(normalized.zoho_org_id)} isRetailerMobile={isRetailerMobile}/>
              <InfoCard icon={Hash} label="Company Code" value={normalized.company_code || "—"} copy={Boolean(normalized.company_code)} isRetailerMobile={isRetailerMobile}/>
            </div>
          </Section>

          <Section title="Audit" isRetailerMobile={isRetailerMobile}> 
            <div className="grid grid-cols-1 gap-3">
              <InfoCard icon={Calendar} label="Created At" value={toLocal(normalized.created_at)} isRetailerMobile={isRetailerMobile}/>
              <InfoCard icon={Calendar} label="Updated At" value={toLocal(normalized.updated_at)} isRetailerMobile={isRetailerMobile}/>
            </div>
          </Section>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4 md:space-y-6">
          <Section title="Bank Details" right={<Badge kind={stepStates[5] ? "green" : "amber"}>{stepStates[5] ? "Complete" : "Pending"}</Badge>} isRetailerMobile={isRetailerMobile}>
            <div className="grid grid-cols-1 gap-3">
              <InfoCard icon={Landmark} label="Bank Name" value={normalized.bank_name} isRetailerMobile={isRetailerMobile}/>
              <InfoCard icon={Landmark} label="Branch Name" value={normalized.branch_name} isRetailerMobile={isRetailerMobile}/>
              <InfoCard icon={Hash} label="Account No" value={normalized.account_no} copy isRetailerMobile={isRetailerMobile}/>
              <InfoCard icon={Hash} label="IFSC Code" value={normalized.ifsc_code || "Not Provided"} copy={Boolean(normalized.ifsc_code)} isRetailerMobile={isRetailerMobile}/>
              <InfoCard icon={FileCheck2} label="Account Type" value={normalized.account_type || "—"} isRetailerMobile={isRetailerMobile}/>
            </div>
          </Section>

          <Section title="Payment" isRetailerMobile={isRetailerMobile}>
            {/* PAYMENT STATUS */}
            {authUser?.role !== 5 && (
            <div className={`rounded-2xl p-4 mb-3 transition-all duration-300
              ${
                isRetailerMobile
                  ? "bg-white/10 backdrop-blur-md border border-white/30"
                  : "border border-blue-100 bg-blue-50"
              }`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-blue-900">
                  Payment Status
                </p>
                <Badge kind={Number(normalized.is_payment_success) === 1 ? "green" : "amber"}>
                  {Number(normalized.is_payment_success) === 1 ? "Success" : "Pending"}
                </Badge>
              </div>
            </div>
            )}

            {/* PAYMENT METHODS */}
            {(entityType === "retailer" || entityType === "retailer_user") && (
              <>
            <div className={`rounded-2xl p-3 space-y-4 transition-all duration-300
              ${
                isRetailerMobile
                  ? "bg-white/10 backdrop-blur-xl border border-white/30"
                  : "border border-slate-200 bg-slate-50"
              }`}>
              {/* COMPANY PAYMENT METHOD */}
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    User Payment Preference
                  </p>
                  <p className="text-[11px] font-semibold text-slate-500">
                    Selected by the company during onboarding
                  </p>
                </div>

                <div className="flex gap-1 flex-wrap">
                  {Number(normalized.pay_now) === 1 && (
                    <Badge kind="green">Pay Now</Badge>
                  )}

                  {Number(normalized.pay_later) === 1 && (
                    <Badge kind="amber">Pay Later</Badge>
                  )}

                  {Number(normalized.pay_now) !== 1 &&
                    Number(normalized.pay_later) !== 1 && (
                      <Badge kind="gray">Not Set</Badge>
                    )}
                </div>
              </div>

              {entityType === "retailer_user" && (
                <div className="flex justify-end">
                  <button
                    onClick={() => setShowUserPaymentModal(true)}
                    className="text-xs font-semibold text-blue-700 border border-blue-200 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100"
                  >
                    Change User Payment
                  </button>
                </div>
              )}

              <hr className="border-slate-200" />

              {authUser?.role !== 5 && (
                <>
                {/* ADMIN PAYMENT OVERRIDE */}
             
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-800">
                    Company Payment Override
                  </p>
                  <p className="text-[11px] font-semibold text-slate-500">
                    Enforced by company (overrides user preference)
                  </p>
                </div>

                <div className="flex items-center gap-1.5 flex-wrap justify-end">
                  {Number(normalized.admin_paynow_status) === 1 && (
                    <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 whitespace-nowrap">
                      Pay Now
                    </span>
                  )}

                  {Number(normalized.admin_paylater_status) === 1 && (
                    <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 whitespace-nowrap">
                      Pay Later
                    </span>
                  )}

                  {Number(normalized.admin_paynow_status) !== 1 &&
                    Number(normalized.admin_paylater_status) !== 1 && (
                      <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600 whitespace-nowrap">
                        Not Set
                      </span>
                    )}
                </div>
              </div>



              <div className="flex justify-end">
                <button
                  onClick={() => setShowPaymentModal(true)}
                  className="text-xs font-semibold text-blue-700 border border-blue-200 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100"
                >
                  Change Payment Method
                </button>
              </div>
                </>
              )}
             
            </div>
             </>
               )}
          </Section>


          {entityType === "retailer" && (
            <Section
              title="Sales Analytics"
              subtitle="Warranty sales & payout summary"
              right={
                <Badge kind="blue">
                  Devices: {saleAnalytics?.total_devices || 0}
                </Badge>
              }
              isRetailerMobile={isRetailerMobile}
            >
              {loadingAnalytics ? (
                <p className="text-sm font-semibold text-slate-500">
                  Loading sales analytics...
                </p>
              ) : saleAnalytics ? (
                <div className="grid grid-cols-1 gap-3">
                  <InfoCard
                    icon={Hash}
                    label="Total Devices Sold"
                    value={saleAnalytics.total_devices}
                    isRetailerMobile={isRetailerMobile}
                  />

                  <InfoCard
                    icon={CreditCard}
                    label="Total Product Value"
                    value={`₹${Number(saleAnalytics.total_product_price || 0).toLocaleString()}`}
                    isRetailerMobile={isRetailerMobile}
                  />

                  {/* <InfoCard
                    icon={User}
                    label="Retailer Payout"
                    value={`₹${Number(saleAnalytics.retailer_payout || 0).toLocaleString()}`}
                  />

                  <InfoCard
                    icon={User}
                    label="Employee Payout"
                    value={`₹${Number(saleAnalytics.employee_payout || 0).toLocaleString()}`}
                  />

                  <InfoCard
                    icon={FileText}
                    label="Other Payout"
                    value={`₹${Number(saleAnalytics.other_payout || 0).toLocaleString()}`}
                  />

                  <InfoCard
                    icon={Building2}
                    label="Company Payout"
                    value={`₹${Number(saleAnalytics.company_payout || 0).toLocaleString()}`}
                  /> */}
                </div>
              ) : (
                <p className="text-sm font-semibold text-slate-500">
                  No sales records found.
                </p>
              )}
            </Section>
          )}

          {/* ✅ Agreement & E-Sign improved */}
          {authUser?.role !== 5 && (
            <Section title="Agreement & E-Sign" subtitle="Links + status (shows signed doc link if available)" isRetailerMobile={isRetailerMobile}>
            <div className="space-y-3">
              <div className="rounded-2xl border border-blue-100 bg-white p-4">
                <div className="flex items-center justify-between gap-2">
                  <p className="text-sm font-semibold text-slate-900">Agreement</p>
                  <Badge kind={agreementUploaded ? "green" : "gray"}>
                    {agreementUploaded ? "Uploaded" : "Pending"}
                  </Badge>
                </div>

                {!agreementUploaded ? (
                  <div className="mt-4 space-y-3">
                    <input
                      type="file"
                      accept=".pdf"
                      onChange={(e) => setEsignFile(e.target.files[0])}
                      className="w-full text-sm"
                    />


                    <button
                      type="button"
                      onClick={handleAgreementUpload}
                      disabled={uploadingEsign}
                      className="w-full rounded-xl py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400"
                    >
                      {uploadingEsign  ? "Uploading..." : "Upload Agreement"}
                    </button>
                  </div>
                ) : (
                  <div className="mt-3 text-green-600 text-sm font-medium">
                    Agreement uploaded successfully ✅
                  </div>
                )}
              </div>


             {agreementUploaded && (
              <section className="flex justify-center">
                <div className="w-full rounded-2xl border border-slate-200 bg-white/80 shadow-sm backdrop-blur p-4 sm:p-6 flex flex-col items-center gap-5">

                  {!aadhaarVerified && (
                    <>

                      <div className="flex items-center gap-3 w-full">
                        <div className="shrink-0 rounded-xl p-3 bg-gradient-to-br from-sky-500/10 to-indigo-500/10">
                          <ShieldCheck className="w-8 h-8 text-sky-600" />
                        </div>
                        <div className="flex-1">
                          <h2 className="text-base md:text-lg font-semibold text-slate-900">
                            Aadhaar e-Sign Verification
                          </h2>
                          <p className="text-xs text-slate-500">
                            Enter the last 4 digits of your Aadhaar. We’ll open a secure e-Sign window.
                          </p>
                        </div>
                      </div>

                      <div className="mt-2 flex justify-center w-full">
                        <div className="flex gap-2">
                          {aadhaarLast4.map((digit, idx) => (
                            <input
                              key={idx}
                              ref={(el) => (aadhaarInputsRef.current[idx] = el)}
                              inputMode="numeric"
                              pattern="[0-9]*"
                              type="text"
                              maxLength={1}
                              value={digit}
                              onChange={(e) =>
                                handleAadhaarBoxChange(idx, e.target.value)
                              }
                              onKeyDown={(e) =>
                                handleAadhaarKeyDown(idx, e)
                              }
                              disabled={aadhaarVerified}
                              className="w-12 h-12 text-center text-lg font-semibold rounded-xl border border-slate-300 bg-white focus:outline-none focus:ring-2 focus:ring-sky-400 disabled:bg-slate-100"
                            />
                          ))}
                        </div>
                      </div>
                    </>
                  )}

                  <div className="min-h-[18px] text-center text-xs">

                    {esignStatusLoading && (
                      <div className="flex justify-center items-center gap-2 text-blue-600 py-2">
                        <svg className="w-4 h-4 animate-spin" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                          />
                        </svg>
                        Checking e-Sign status...
                      </div>
                    )}

                    {!esignStatusLoading && (
                      <>
                        {aadhaarError ? (
                          <span className="text-red-600">{aadhaarError}</span>
                        ) : aadhaarVerified ? (
                          <div className="flex flex-col items-center gap-2 text-green-600">
                            <CheckCircle2 className="w-16 h-16" />
                            <span>Verified ✅ e-Sign completed</span>
                          </div>
                        ) : aadhaarStatus === "verifying" ? (
                          <span className="text-slate-500">
                            Waiting for you to complete e-Sign in the popup...
                          </span>
                        ) : (
                          <span className="text-slate-500">
                            We’ll redirect you to a secure UIDAI-compliant e-Sign partner.
                          </span>
                        )}
                      </>
                    )}
                  </div>

                  {!aadhaarVerified && (
                    <button
                      type="button"
                      onClick={verifyAadhaar}
                      disabled={
                        !aadhaarValid ||
                        aadhaarStatus === "loading" ||
                        aadhaarStatus === "verifying" ||
                        esignStatusLoading
                      }
                      className="w-full max-w-xs rounded-xl py-2.5 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-slate-400"
                    >
                      {esignStatusLoading
                        ? "Checking Status..."
                        : "Verify & Open e-Sign"}
                    </button>
                  )}

                  {!aadhaarVerified && (
                    <p className="text-[11px] text-center text-slate-500">
                      By continuing, you consent to Aadhaar-based e-Sign for this agreement.
                    </p>
                  )}

                </div>
              </section>
              )}
            </div>
          </Section>
          )}

        </div>
      </div>
        )}

        {entityType === "company" && currentTab === 2 && (<AssignCompanyProducts companyId={id} />)}

        {currentTab === 3 && (
            <CompanyUsers
              entityType={entityType}
              entityId={id}
              isRetailerMobile={isRetailerMobile}
            />
          )}

        {currentTab === 4 && entityType !== "retailer_user" && (
          <InvoicesView
            fromUserView={true}
            selectedUserId={id}
            selectedUserRole={
              entityType === "company"
                ? 2
                : entityType === "agent"
                ? 4
                : entityType === "retailer"
                ? 5
                : 6
            }
            data={data}

          />
        )}

        {currentTab === 0 && <CompanySales fromUserView={true}
            selectedUserId={id}
            selectedUserRole={
              entityType === "company"
                ? 2
                : entityType === "agent"
                ? 4
                : entityType === "retailer"
                ? 5
                : 6
            }
            
              balanceAmount={balanceAmount}
              setBalanceAmount={setBalanceAmount}
            />}

        {currentTab === 5 && entityType === "retailer" && (
          <RemarkChat
            entityType={entityType}
            entityId={id}
          />
        )}

      <Outlet/>

      {/* Layout */}
      
      {/* <div className="mt-8 text-center text-xs font-semibold text-slate-500">
        Blue/White theme • Responsive Tailwind • Optimized KYC & E-Sign UI
      </div> */}

      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">
              Update Payment Method
            </h3>

            <p className="mt-1 text-sm font-semibold text-slate-500">
              Select one option. This overrides user payment behavior.
            </p>

            <div className="mt-4 space-y-3">
              {/* PAY NOW */}
              <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 cursor-pointer hover:bg-slate-100">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Pay Now</p>
                  <p className="text-xs font-semibold text-slate-500">
                    Payment required immediately
                  </p>
                </div>

                <input
                  type="checkbox"
                  checked={paymentSelection.paynow}
                  disabled={updatingPayment}
                  onChange={(e) =>
                    setPaymentSelection((prev) => ({
                      ...prev,
                      paynow: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 accent-emerald-600"
                />
              </label>

              {/* PAY LATER */}
              <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 cursor-pointer hover:bg-slate-100">
                <div>
                  <p className="text-sm font-semibold text-slate-900">Pay Later</p>
                  <p className="text-xs font-semibold text-slate-500">
                    Payment deferred to a later stage
                  </p>
                </div>

                <input
                  type="checkbox"
                  checked={paymentSelection.paylater}
                  disabled={updatingPayment}
                  onChange={(e) =>
                    setPaymentSelection((prev) => ({
                      ...prev,
                      paylater: e.target.checked,
                    }))
                  }
                  className="h-4 w-4 accent-amber-600"
                />
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                disabled={updatingPayment}
                onClick={() => {
                  setShowPaymentModal(false);
                  // setSelectedPayment(null);
                }}
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
              >
                Cancel
              </button>

              <button
                disabled={
                  updatingPayment ||
                  (!paymentSelection.paynow && !paymentSelection.paylater)
                }
                onClick={() => handleAdminPaymentUpdate(paymentSelection)}
                className="w-full sm:w-auto rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
              >
                Confirm
              </button>

            </div>
          </div>
        </div>
      )}

      {showUserPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">
              Update User Payment Preference
            </h3>

            <p className="mt-1 text-sm font-semibold text-slate-500">
              Select payment method for user
            </p>

            <div className="mt-4 space-y-3">
              {/* PAY NOW */}
              <label className="flex items-center justify-between rounded-xl border px-4 py-3">
                <span>Pay Now</span>
                <input
                  type="checkbox"
                  checked={userPaymentSelection.paynow}
                  onChange={(e) =>
                    setUserPaymentSelection((prev) => ({
                      ...prev,
                      paynow: e.target.checked,
                    }))
                  }
                />
              </label>

              {/* PAY LATER */}
              <label className="flex items-center justify-between rounded-xl border px-4 py-3">
                <span>Pay Later</span>
                <input
                  type="checkbox"
                  checked={userPaymentSelection.paylater}
                  onChange={(e) =>
                    setUserPaymentSelection((prev) => ({
                      ...prev,
                      paylater: e.target.checked,
                    }))
                  }
                />
              </label>
            </div>

            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={() => setShowUserPaymentModal(false)}
                className="border px-4 py-2 rounded-xl"
              >
                Cancel
              </button>

              <button
                onClick={() => handleUserPaymentUpdate(userPaymentSelection)}
                disabled={
                  !userPaymentSelection.paynow &&
                  !userPaymentSelection.paylater
                }
                className="bg-blue-600 text-white px-4 py-2 rounded-xl"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showLogoutConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-semibold text-slate-900">
              {Number(normalized.is_logout) === 1
                ? "Remove Force Logout"
                : "Force Logout User"}
            </h3>

            <p className="mt-2 text-sm font-semibold text-slate-600">
              {Number(normalized.is_logout) === 1
                ? "This will allow the user to log in again."
                : "This will immediately log the user out from all active sessions."}
            </p>

            <div className="mt-6 flex justify-end gap-2">
              <button
                disabled={forcingLogout}
                onClick={() => setShowLogoutConfirm(false)}
                className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
              >
                Cancel
              </button>

              <button
                disabled={forcingLogout}
                onClick={handleToggleLogout}
                className={`rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50
                  ${
                    Number(normalized.is_logout) === 1
                      ? "bg-emerald-600 hover:bg-emerald-700"
                      : "bg-rose-600 hover:bg-rose-700"
                  }
                `}
              >
                {forcingLogout
                  ? "Please wait…"
                  : Number(normalized.is_logout) === 1
                  ? "Remove Logout"
                  : "Confirm Logout"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showWalletConfirm && (
  <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
    <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
      
      <h3 className="text-lg font-semibold text-slate-900">
        {Number(normalized.allow_wallet) === 1
          ? "Disable Wallet Access"
          : "Allow Wallet Access"}
      </h3>

      <p className="mt-2 text-sm font-semibold text-slate-600">
        Do you want to{" "}
        {Number(normalized.allow_wallet) === 1 ? "disable" : "allow"}{" "}
        <span className="font-bold">
          {normalized.business_name}
        </span>{" "}
        to use your wallet balance?
      </p>

      <div className="mt-6 flex justify-end gap-2">
        <button
          disabled={updatingWallet}
          onClick={() => setShowWalletConfirm(false)}
          className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
        >
          Cancel
        </button>

        <button
          disabled={updatingWallet}
          onClick={handleWalletToggle}
          className={`rounded-xl px-4 py-2 text-sm font-semibold text-white
            ${
              Number(normalized.allow_wallet) === 1
                ? "bg-rose-600 hover:bg-rose-700"
                : "bg-emerald-600 hover:bg-emerald-700"
            }
          `}
        >
          {updatingWallet
            ? "Please wait..."
            : Number(normalized.allow_wallet) === 1
            ? "Disable"
            : "Allow"}
        </button>
      </div>
    </div>
  </div>
)}

    </div>
  );
}


// ********************************************full working code***************************************************************8


// import React, { useEffect, useMemo } from "react";
// import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
// import {
//   ArrowLeft,
//   BadgeCheck,
//   Building2,
//   Calendar,
//   CheckCircle2,
//   ClipboardCopy,
//   CreditCard,
//   FileCheck2,
//   FileText,
//   Hash,
//   Landmark,
//   Link as LinkIcon,
//   Mail,
//   MapPin,
//   Phone,
//   ShieldCheck,
//   User,
//   XCircle,
//   ExternalLink,
//   Shield,
// } from "lucide-react";
// import CompanyTabs from "../../Component/AdminComponents/CompanyTabs";

// import AgentsView from "../Main/Admin/AgentsView";
// import InvoicesView from "../Main/Retailer/InvoicesView";
// import CompanySales from "./RetailerDash/CompanySales";
// import AssignCompanyProducts from "./AssignProduct/AssignCompanyProducts";
// import CompanyUsers from "./CompanyUsers";
// import axios from "axios";
// import { BASE_URL } from "../../config/api";
// import toast from "react-hot-toast";
// import RemarkChat from "./RemarkChat";

// const steps = [
//   { id: 1, name: "Verify Email & Phone" },
//   { id: 2, name: "Payment" },
//   { id: 3, name: "PAN Verification" },
//   { id: 4, name: "GST Details" },
//   { id: 5, name: "Bank Details" },
//   { id: 6, name: "Agreement" },
//   { id: 7, name: "E-Sign Verify" },
// ];

// const tone = {
//   blue: "bg-blue-50 text-blue-700 border-blue-200",
//   green: "bg-emerald-50 text-emerald-700 border-emerald-200",
//   red: "bg-rose-50 text-rose-700 border-rose-200",
//   gray: "bg-slate-50 text-slate-700 border-slate-200",
//   amber: "bg-amber-50 text-amber-700 border-amber-200",
// };

// function safeJsonParse(value) {
//   try {
//     if (!value) return null;
//     if (typeof value === "object") return value;
//     return JSON.parse(value);
//   } catch {
//     return null;
//   }
// }

// function fmt(value) {
//   if (value === null || value === undefined || value === "") return "—";
//   return String(value);
// }

// function toLocal(dt) {
//   if (!dt) return "—";
//   const d = new Date(dt);
//   return Number.isNaN(d.getTime()) ? String(dt) : d.toLocaleString();
// }

// function Badge({ kind = "blue", children }) {
//   return (
//     <span className={`inline-flex items-center rounded-full border px-1 py-1 text-xs font-semibold ${tone[kind]}`}>
//       {children}
//     </span>
//   );
// }

// function Pill({ ok, yes = "Yes", no = "No" }) {
//   return ok ? (
//     <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-1 text-xs font-bold text-emerald-700">
//       <CheckCircle2 className="h-3.5 w-3.5" />
//       {yes}
//     </span>
//   ) : (
//     <span className="inline-flex items-center gap-1 rounded-full bg-slate-50 px-2 py-1 text-xs font-bold text-slate-700">
//       <XCircle className="h-3.5 w-3.5" />
//       {no}
//     </span>
//   );
// }

// function CopyBtn({ value }) {
//   const [copied, setCopied] = React.useState(false);

//   if (!value || value === "—") return null;

//   const handleCopy = async () => {
//     await navigator.clipboard.writeText(String(value));
//     setCopied(true);
//     setTimeout(() => setCopied(false), 1500);
//   };

//   return (
//     <div className="relative">
//       <button
//         type="button"
//         onClick={handleCopy}
//         className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100"
//       >
//         <ClipboardCopy className="h-4 w-4" />
//         Copy
//       </button>

//       {/* Tooltip */}
//       {copied && (
//         <span className="absolute -top-7 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-md bg-black-900 px-2 py-1 text-[10px] font-semibold text-white shadow">
//           Copied ✓
//         </span>
//       )}
//     </div>
//   );
// }


// function InfoCard({ icon: Icon, label, value, copy = false, right }) {
//   const v = fmt(value);
//   return (
//     <div className="flex items-start gap-3 rounded-2xl border border-blue-100 bg-white p-4 shadow-sm">
//       <div className="mt-0.5 rounded-xl bg-blue-50 p-2 text-blue-700">
//         <Icon className="h-4 w-4" />
//       </div>
//       <div className="min-w-0 flex-1">
//         <p className="text-xs font-semibold text-slate-500">{label}</p>
//         <p className="mt-0.5 break-words text-sm font-semibold text-slate-900">{v}</p>
//       </div>
//       <div className="flex items-center gap-2">
//         {right}
//         {copy && v !== "—" ? <CopyBtn value={v} /> : null}
//       </div>
//     </div>
//   );
// }

// function Section({ title, subtitle, right, children }) {
//   return (
//     <div className="min-w-[375px] rounded-2xl border border-blue-100 bg-white p-5 shadow-sm">
//       <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
//         <div>
//           <h2 className="text-base font-semibold text-slate-900">{title}</h2>
//           {subtitle ? <p className="mt-1 text-sm font-semibold text-slate-600">{subtitle}</p> : null}
//         </div>
//         {right}
//       </div>
//       {children}
//     </div>
//   );
// }

// // Step completion rules
// function getStepStates(normalized) {
//   const emailPhoneDone = Boolean(normalized?.is_mail_verified) && Boolean(normalized?.is_wa_verified);
//   const paymentDone = Number(normalized?.is_payment_success) === 1;
//   const panDone = Number(normalized?.pan_verified) === 1;
//   const gstDone = Boolean(normalized?.gst);
//   const bankDone = Boolean(normalized?.account_no) && Boolean(normalized?.bank_name) && Boolean(normalized?.branch_name);
//   const agreementDone = Number(normalized?.is_verified) >= 6; // replace if you have explicit flag
//   const esignDone = Number(normalized?.esign_verified) === 1;

//   return { 1: emailPhoneDone, 2: paymentDone, 3: panDone, 4: gstDone, 5: bankDone, 6: agreementDone, 7: esignDone };
// }

// function Stepper({ currentStage, states }) {
//   return (
//     <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
//       {steps.map((s) => {
//         const done = Boolean(states?.[s.id]);
//         const active = Number(currentStage) === s.id;

//         return (
//           <div
//             key={s.id}
//             className={[
//               "flex items-center justify-between rounded-2xl border p-4",
//               done ? "border-emerald-200 bg-emerald-50" : active ? "border-blue-200 bg-blue-50" : "border-blue-100 bg-white",
//             ].join(" ")}
//           >
//             <div className="flex items-center gap-3">
//               <div
//                 className={[
//                   "flex h-10 w-10 items-center justify-center rounded-xl border",
//                   done
//                     ? "border-emerald-200 bg-white text-emerald-700"
//                     : active
//                       ? "border-blue-200 bg-white text-blue-700"
//                       : "border-blue-100 bg-blue-50 text-blue-700",
//                 ].join(" ")}
//               >
//                 {done ? <CheckCircle2 className="h-5 w-5" /> : <span className="text-sm font-semibold">{s.id}</span>}
//               </div>
//               <div>
//                 <p className="text-sm font-semibold text-slate-900">{s.name}</p>
//                 <p className="text-xs font-semibold text-slate-500">{done ? "Completed" : active ? "In progress" : "Pending"}</p>
//               </div>
//             </div>

//             {done ? <Badge kind="green">Done</Badge> : active ? <Badge kind="blue">Active</Badge> : <Badge kind="gray">Pending</Badge>}
//           </div>
//         );
//       })}
//     </div>
//   );
// }

// function LinkBtn({ href, children }) {
//   if (!href) return null;
//   return (
//     <a
//       href={href}
//       target="_blank"
//       rel="noreferrer"
//       className="inline-flex items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
//     >
//       <ExternalLink className="h-4 w-4" />
//       {children}
//     </a>
//   );
// }

// export default function UserDetails() {
  
//   const navigate = useNavigate();
//   const { id } = useParams();
//   console.log("id from promoter: ",id)
//   const location = useLocation();
//   const { entityType, data } = location.state || {};
//   const [saleAnalytics, setSaleAnalytics] = React.useState(null);
//   const [loadingAnalytics, setLoadingAnalytics] = React.useState(false);
//   const [showPaymentModal, setShowPaymentModal] = React.useState(false);
//   const [updatingPayment, setUpdatingPayment] = React.useState(false);
//   // const [selectedPayment, setSelectedPayment] = React.useState(null);
//   const [currentTab, setCurrentTab] = React.useState(0);

//   const [showLogoutConfirm, setShowLogoutConfirm] = React.useState(false);
// const [forcingLogout, setForcingLogout] = React.useState(false);

//   const normalized = useMemo(() => {
//   if (!entityType || !data) return null;

//   if (entityType === "company") {
//     return {
//       ...data, // ✅ include ALL company fields
//       id: data.id,
//       name: data.business_name,
//       contactPerson: data.contact_person,
//       phone: data.contact_phone,
//       email: data.contact_email,
//       role: "Company",
//     };
//   }

//   if (entityType === "agent") {
//     return {
//       ...data, // ✅ include ALL agent fields
//       id: data.id,
//       name: data.business_name || data.contact_person,
//       contactPerson: data.contact_person,
//       phone: data.contact_phone,
//       email: data.contact_email,
//       role: "Agent",
//     };
//   }

//   if (entityType === "retailer") {
//     return {
//       ...data,
//       id: data.id,
//       business_name: data.name || data.business_name,
//       contact_person: data.contactPerson || data.contact_person,
//       contact_phone: data.mobile || data.contact_phone,
//       contact_email: data.email || data.contact_email,
//       role: "Retailer",
//     };
//   }

//   return null;
// }, [entityType, data]);


// useEffect(() => {
//   if (!id || entityType !== "retailer") return;

//   async function fetchSaleAnalytics() {
//     try {
//       setLoadingAnalytics(true);

//       const res = await axios.get(
//         `${BASE_URL}warranty/sale-records?retailer_id=${id}`
//       );

//       if (res.data?.success) {
//         setSaleAnalytics(res.data.analytics);
//       }
//     } catch (error) {
//       console.error("Failed to fetch sale analytics", error);
//     } finally {
//       setLoadingAnalytics(false);
//     }
//   }

//   fetchSaleAnalytics();
// }, [id, entityType]);

//   const panData = useMemo(() => safeJsonParse(normalized?.pan_json), [normalized?.pan_json]);
//   const esignData = useMemo(() => safeJsonParse(normalized?.esign_json), [normalized?.esign_json]);

//   const stepStates = useMemo(() => getStepStates(normalized), [normalized]);

//   // Signed document link support (if backend provides later)
//   // ✅ If you have a real signed doc URL, add it in payload as e.g. `esign_signed_doc_url`
//   const signedDocUrl =
//     normalized?.esign_signed_doc_url ||
//     esignData?.signed_document_url ||
//     esignData?.signed_doc_url ||
//     null;

//   // Signing link (your payload includes this)
//   const signingLink = esignData?.signing_link || null;

//   const mailOk = Boolean(normalized?.is_mail_verified);
//   const waOk = Boolean(normalized?.is_wa_verified);

//   const gstType = normalized?.business_type ? String(normalized.business_type).toUpperCase() : "—";
//   const entityType1 =
//     panData?.type || normalized?.company_type || normalized?.entity_type || "—"; // add company_type in payload if you have it


//   useEffect(() => {
//     setCurrentTab(0); // always go to Overview
//   }, [id]);

//   const [paymentSelection, setPaymentSelection] = React.useState({
//     paynow: Number(normalized?.admin_paynow_status) === 1,
//     paylater: Number(normalized?.admin_paylater_status) === 1,
//   });


//   useEffect(() => {
//     if (showPaymentModal && normalized) {
//       setPaymentSelection({
//         paynow: Number(normalized.admin_paynow_status) === 1,
//         paylater: Number(normalized.admin_paylater_status) === 1,
//       });
//     }
//   }, [showPaymentModal, normalized]);



//   const handleAdminPaymentUpdate = async (selection) => {
//   const payload = {
//     admin_paynow_status: selection.paynow ? 1 : 0,
//     admin_paylater_status: selection.paylater ? 1 : 0,
//     admin_status_change_date: new Date().toISOString().split("T")[0],
//   };

//   try {
//     setUpdatingPayment(true);

//     const res = await fetch(
//       `${BASE_URL}company/update-fields/${normalized.id}`,
//       {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify(payload),
//       }
//     );

//     const json = await res.json();

//     if (json.status) {
//       toast.success("Payment override updated successfully");

//       // ✅ update UI immediately (NO reload)
//       setShowPaymentModal(false);

//       // IMPORTANT: update normalized values locally
//       normalized.admin_paynow_status = payload.admin_paynow_status;
//       normalized.admin_paylater_status = payload.admin_paylater_status;
//     } else {
//       toast.error("Failed to update payment override");
//     }
//   } catch (err) {
//     console.error(err);
//     toast.error("Something went wrong while updating payment");
//   } finally {
//     setUpdatingPayment(false);
//   }
// };

// const handleToggleLogout = async () => {
//   const nextLogoutState = Number(normalized.is_logout) === 1 ? 0 : 1;

//   try {
//     setForcingLogout(true);

//     const res = await axios.post(`${BASE_URL}common/logout`, {
//       id: normalized.id,
//       is_logout: nextLogoutState,
//     });

//     if (res.data?.status || res.data?.success) {
//       toast.success(
//         nextLogoutState === 1
//           ? "User force-logged out successfully"
//           : "Force logout removed successfully"
//       );

//       // 🔁 update UI instantly
//       normalized.is_logout = nextLogoutState;
//       setShowLogoutConfirm(false);
//     } else {
//       toast.error("Failed to update logout status");
//     }
//   } catch (err) {
//     console.error(err);
//     toast.error("Something went wrong while updating logout status");
//   } finally {
//     setForcingLogout(false);
//   }
// };


//   if (!entityType || !data || !normalized) {
//     return (
//       <div className="mx-auto max-w-5xl p-4 md:p-8">
//         <div className="rounded-2xl border border-blue-100 bg-white p-6 text-center shadow-sm">
//           <p className="text-base font-semibold text-slate-900">No user data found</p>
//           <p className="mt-1 text-sm font-semibold text-slate-600">
//             Refresh clears route state. Go back and open details again (Company ID: <span className="font-bold">{id}</span>).
//           </p>
//           <button
//             type="button"
//             onClick={() => navigate(-1)}
//             className="mt-4 inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
//           >
//             <ArrowLeft className="h-4 w-4" />
//             Go Back
//           </button>
//         </div>
//       </div>
//     );
//   }


//   return (
//     <div className="mx-auto  p-4 md:p-8">
//       {/* Header */}
//       <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
//         <div className="min-w-0">
//           <div className="flex flex-wrap items-center gap-2">
            
//             <h1 className="truncate text-xl font-semibold text-slate-900 md:text-2xl">{fmt(normalized.business_name)}</h1>
            
//             <Badge kind={Number(normalized.is_payment_success) === 1 ? "green" : "amber"}>
//               {Number(normalized.is_payment_success) === 1 ? "Payment Success" : "Payment Pending"}
//             </Badge>
//             <Badge kind={Number(normalized.esign_verified) === 1 ? "green" : "gray"}>
//               {Number(normalized.esign_verified) === 1 ? "E-Sign Verified" : "E-Sign Pending"}
//             </Badge>
//           </div>

//           <Badge
//             kind="blue"
//             className="text-2xl font-semibold px-4 py-2"
//           >
//             Role: {normalized.role}
//           </Badge>

//           <div className="mt-2 flex flex-wrap items-center gap-2 text-sm font-semibold text-slate-600">
//             <span>
//               Trade Name: <span className="font-semibold text-slate-900">{fmt(normalized.trade_name)}</span>
//             </span>
//             <span className="text-slate-300">•</span>
//             <span>
//               Created: <span className="font-semibold text-slate-900">{toLocal(normalized.created_at)}</span>
//             </span>
//           </div>
//         </div>

//         <div className="flex flex-wrap gap-2">
//           {/* <button
//             type="button"
//             onClick={() => navigate(-1)}
//             className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50"
//           >
//             <ArrowLeft className="h-4 w-4" />
//             Back
//           </button>

//           <button
//             type="button"
//             onClick={() => navigator.clipboard.writeText(JSON.stringify(normalized, null, 2))}
//             className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
//           >
//             <ClipboardCopy className="h-4 w-4" />
//             Copy Payload
//           </button> */}

//           <button
//             onClick={() => setShowLogoutConfirm(true)}
//             className={`inline-flex items-center gap-2 rounded-xl border px-4 py-2 text-sm font-semibold
//               ${
//                 Number(normalized.is_logout) === 1
//                   ? "border-emerald-200 bg-emerald-50 text-emerald-700 hover:bg-emerald-100"
//                   : "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
//               }
//             `}
//           >
//             <Shield className="h-4 w-4" />
//             {Number(normalized.is_logout) === 1 ? "Remove Force Logout" : "Force Logout"}
//           </button>

//         </div>
//       </div>

//       <CompanyTabs currentTab={currentTab} setCurrentTab={setCurrentTab} entityType={entityType} />

//       {currentTab === 0 && (
//         <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
//         {/* Main */}
//         <div className="space-y-6 lg:col-span-2">
//           <Section
//             title="Onboarding Progress"
//             subtitle="Clear step tracker based on your payload flags"
//             right={
//               <div className="flex flex-wrap items-center gap-2">
//                 <Badge kind="blue">
//                   Stage: <span className="ml-1 font-semibold">{fmt(normalized.is_verified)}</span>
//                 </Badge>
//                 <Badge kind={mailOk ? "green" : "gray"}>
//                   <Mail className="mr-1 h-3.5 w-3.5" /> Email {mailOk ? "Verified" : "Pending"}
//                 </Badge>
//                 <Badge kind={waOk ? "green" : "gray"}>
//                   <Phone className="mr-1 h-3.5 w-3.5" /> Phone/WA {waOk ? "Verified" : "Pending"}
//                 </Badge>
//               </div>
//             }
//           >
//             <Stepper currentStage={normalized.is_verified} states={stepStates} />
//           </Section>

//           <Section title="Company & Contact">
//             <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
//               <InfoCard icon={Building2} label="Business Name" value={normalized.business_name} copy />
//               <InfoCard icon={User} label="Contact Person" value={normalized.contact_person} copy />
//               <InfoCard icon={Phone} label="Contact Phone" value={normalized.contact_phone} copy />
//               <InfoCard icon={Mail} label="Contact Email" value={normalized.contact_email} copy />
//             </div>
//           </Section>

//           <Section title="Address">
//             <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
//               <InfoCard icon={MapPin} label="Address Line 1" value={normalized.address_line1} />
//               <InfoCard icon={MapPin} label="Address Line 2" value={normalized.address_line2} />
//               <InfoCard icon={MapPin} label="City" value={normalized.city} />
//               <InfoCard icon={MapPin} label="District" value={normalized.district} />
//               <InfoCard icon={MapPin} label="State" value={normalized.state} />
//               <InfoCard icon={Hash} label="Pincode" value={normalized.pincode} copy />
//             </div>
//           </Section>

//           {/* ✅ KYC improved */}
//           <Section
//             title="KYC: PAN & GST"
//             subtitle="PAN verification + GST details + business classifications"
//             right={<Badge kind={stepStates[3] ? "green" : "amber"}>{stepStates[3] ? "PAN Done" : "PAN Pending"}</Badge>}
//           >
//             <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
//               <InfoCard icon={ShieldCheck} label="PAN Number" value={normalized.pan} copy />
//               <InfoCard
//                 icon={Shield}
//                 label="PAN Verified"
//                 value={Number(normalized.pan_verified) === 1 ? "Yes" : "No"}
//                 right={<Pill ok={Number(normalized.pan_verified) === 1} />}
//               />

//               <InfoCard icon={FileText} label="GST Number" value={normalized.gst || "Not Provided"} copy={Boolean(normalized.gst)} />
//               <InfoCard icon={FileCheck2} label="GST Type" value={gstType} />

//               <InfoCard icon={Building2} label="Business Entity Type" value={entityType1} />
//               <InfoCard icon={FileText} label="Role" value={fmt(normalized.role)} />

//               {/* PAN JSON extracted */}
//               {panData ? (
//                 <div className="md:col-span-2 rounded-2xl border border-blue-100 bg-blue-50 p-4">
//                   <div className="flex flex-wrap items-center justify-between gap-2">
//                     <div>
//                       <p className="text-sm font-semibold text-blue-900">PAN Verification Extract</p>
//                       <p className="mt-0.5 text-xs font-semibold text-blue-800/70">
//                         Reference ID: <span className="font-semibold">{fmt(panData.reference_id)}</span> • Status:{" "}
//                         <span className="font-semibold">{fmt(panData.status)}</span>
//                       </p>
//                     </div>
//                     <Badge kind={String(panData.status || "").toUpperCase() === "VALID" ? "green" : "blue"}>
//                       {fmt(panData.status)}
//                     </Badge>
//                   </div>

//                   <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
//                     <InfoCard icon={BadgeCheck} label="Registered Name" value={panData.registered_name} />
//                     <InfoCard icon={BadgeCheck} label="PAN Card Name" value={panData.name_pan_card} />
//                     <InfoCard icon={User} label="Name Provided" value={panData.name_provided} />
//                     <InfoCard icon={Hash} label="Masked Aadhaar" value={panData.masked_aadhaar_number} copy />

//                     <InfoCard icon={Calendar} label="DOB" value={panData.date_of_birth} />
//                     <InfoCard icon={User} label="Gender" value={panData.gender} />
//                     <InfoCard icon={Phone} label="Mobile (masked)" value={panData.mobile_number} />
//                     <InfoCard icon={Mail} label="Email (masked)" value={panData.email} />

//                     <InfoCard
//                       icon={ShieldCheck}
//                       label="Aadhaar Linked"
//                       value={panData.aadhaar_linked ? "Yes" : "No"}
//                       right={<Pill ok={Boolean(panData.aadhaar_linked)} />}
//                     />
//                     <InfoCard icon={FileText} label="PAN Holder Type" value={panData.type} />
//                   </div>

//                   {panData?.address?.full_address ? (
//                     <div className="mt-3 rounded-2xl border border-blue-100 bg-white p-4">
//                       <p className="text-xs font-semibold text-slate-500">PAN Address</p>
//                       <p className="mt-1 text-sm font-semibold text-slate-900">{panData.address.full_address}</p>
//                       <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
//                         <InfoCard icon={MapPin} label="City" value={panData.address.city} />
//                         <InfoCard icon={MapPin} label="State" value={panData.address.state} />
//                         <InfoCard icon={Hash} label="Pincode" value={panData.address.pincode} />
//                         <InfoCard icon={MapPin} label="Country" value={panData.address.country} />
//                       </div>
//                     </div>
//                   ) : null}

//                   <details className="mt-3 rounded-2xl border border-blue-100 bg-white p-4">
//                     <summary className="cursor-pointer text-sm font-semibold text-blue-700">
//                       View Raw PAN JSON
//                     </summary>
//                     <pre className="mt-3 max-h-96 overflow-auto rounded-xl bg-slate-900 p-4 text-xs text-slate-100">
// {JSON.stringify(panData, null, 2)}
//                     </pre>
//                   </details>
//                 </div>
//               ) : (
//                 <div className="md:col-span-2 rounded-2xl border border-blue-100 bg-white p-4">
//                   <p className="text-sm font-bold text-slate-900">PAN JSON not available</p>
//                   <p className="mt-1 text-xs font-semibold text-slate-500">Field pan_json is empty or invalid JSON.</p>
//                 </div>
//               )}
//             </div>
//           </Section>

//           <div className="flex gap-5">
//             <Section title="Account Details">
//             <div className="grid grid-cols-1 gap-3">
//               <InfoCard icon={Building2} label="Zoho ID" value={normalized.zoho_id || "—"} copy={Boolean(normalized.zoho_id)} />
//               <InfoCard icon={Building2} label="Zoho Org ID" value={normalized.zoho_org_id || "Not Connected"} copy={Boolean(normalized.zoho_org_id)} />
//               <InfoCard icon={Hash} label="Company Code" value={normalized.company_code || "—"} copy={Boolean(normalized.company_code)} />
//             </div>
//           </Section>

//           <Section title="Audit"> 
//             <div className="grid grid-cols-1 gap-3">
//               <InfoCard icon={Calendar} label="Created At" value={toLocal(normalized.created_at)} />
//               <InfoCard icon={Calendar} label="Updated At" value={toLocal(normalized.updated_at)} />
//             </div>
//           </Section>
//           </div>
//         </div>

//         {/* Sidebar */}
//         <div className="space-y-6">
//           <Section title="Bank Details" right={<Badge kind={stepStates[5] ? "green" : "amber"}>{stepStates[5] ? "Complete" : "Pending"}</Badge>}>
//             <div className="grid grid-cols-1 gap-3">
//               <InfoCard icon={Landmark} label="Bank Name" value={normalized.bank_name} />
//               <InfoCard icon={Landmark} label="Branch Name" value={normalized.branch_name} />
//               <InfoCard icon={Hash} label="Account No" value={normalized.account_no} copy />
//               <InfoCard icon={Hash} label="IFSC Code" value={normalized.ifsc_code || "Not Provided"} copy={Boolean(normalized.ifsc_code)} />
//               <InfoCard icon={FileCheck2} label="Account Type" value={normalized.account_type || "—"} />
//             </div>
//           </Section>

//           <Section title="Payment">
//             {/* PAYMENT STATUS */}
//             <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 mb-3">
//               <div className="flex items-center justify-between">
//                 <p className="text-sm font-semibold text-blue-900">
//                   Payment Status
//                 </p>
//                 <Badge kind={Number(normalized.is_payment_success) === 1 ? "green" : "amber"}>
//                   {Number(normalized.is_payment_success) === 1 ? "Success" : "Pending"}
//                 </Badge>
//               </div>
//             </div>

//             {/* PAYMENT METHODS */}
//             {entityType === "retailer" && (
//               <>
//             <div className="rounded-2xl border border-slate-200 bg-slate-50 p-3 space-y-4">
//               {/* COMPANY PAYMENT METHOD */}
//               <div className="flex items-center justify-between">
//                 <div>
//                   <p className="text-sm font-semibold text-slate-800">
//                     User Payment Preference
//                   </p>
//                   <p className="text-[11px] font-semibold text-slate-500">
//                     Selected by the company during onboarding
//                   </p>
//                 </div>

//                 <Badge kind={normalized.pay_now === 1 ? "green" : "amber"}>
//                   {normalized.pay_now === 1 ? "Pay Now" : "Pay Later"}
//                 </Badge>
//               </div>

//               <hr className="border-slate-200" />

//               {/* ADMIN PAYMENT OVERRIDE */}
             
//               <div className="flex items-center justify-between">
//               <div>
//                 <p className="text-sm font-semibold text-slate-800">
//                   Company Payment Override
//                 </p>
//                 <p className="text-[11px] font-semibold text-slate-500">
//                   Enforced by company (overrides user preference)
//                 </p>
//               </div>

//               <div className="flex items-center gap-1.5 flex-wrap justify-end">
//                 {Number(normalized.admin_paynow_status) === 1 && (
//                   <span className="inline-flex items-center rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700 whitespace-nowrap">
//                     Pay Now
//                   </span>
//                 )}

//                 {Number(normalized.admin_paylater_status) === 1 && (
//                   <span className="inline-flex items-center rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700 whitespace-nowrap">
//                     Pay Later
//                   </span>
//                 )}

//                 {Number(normalized.admin_paynow_status) !== 1 &&
//                   Number(normalized.admin_paylater_status) !== 1 && (
//                     <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-semibold text-slate-600 whitespace-nowrap">
//                       Not Set
//                     </span>
//                   )}
//               </div>
//             </div>



//               <div className="flex justify-end">
//                 <button
//                   onClick={() => setShowPaymentModal(true)}
//                   className="text-xs font-semibold text-blue-700 border border-blue-200 bg-blue-50 px-3 py-1.5 rounded-lg hover:bg-blue-100"
//                 >
//                   Change Payment Method
//                 </button>
//               </div>
             
//             </div>
//              </>
//                )}
//           </Section>


//           {entityType === "retailer" && (
//             <Section
//               title="Sales Analytics"
//               subtitle="Warranty sales & payout summary"
//               right={
//                 <Badge kind="blue">
//                   Devices: {saleAnalytics?.total_devices || 0}
//                 </Badge>
//               }
//             >
//               {loadingAnalytics ? (
//                 <p className="text-sm font-semibold text-slate-500">
//                   Loading sales analytics...
//                 </p>
//               ) : saleAnalytics ? (
//                 <div className="grid grid-cols-1 gap-3">
//                   <InfoCard
//                     icon={Hash}
//                     label="Total Devices Sold"
//                     value={saleAnalytics.total_devices}
//                   />

//                   <InfoCard
//                     icon={CreditCard}
//                     label="Total Product Value"
//                     value={`₹${Number(saleAnalytics.total_product_price || 0).toLocaleString()}`}
//                   />

//                   {/* <InfoCard
//                     icon={User}
//                     label="Retailer Payout"
//                     value={`₹${Number(saleAnalytics.retailer_payout || 0).toLocaleString()}`}
//                   />

//                   <InfoCard
//                     icon={User}
//                     label="Employee Payout"
//                     value={`₹${Number(saleAnalytics.employee_payout || 0).toLocaleString()}`}
//                   />

//                   <InfoCard
//                     icon={FileText}
//                     label="Other Payout"
//                     value={`₹${Number(saleAnalytics.other_payout || 0).toLocaleString()}`}
//                   />

//                   <InfoCard
//                     icon={Building2}
//                     label="Company Payout"
//                     value={`₹${Number(saleAnalytics.company_payout || 0).toLocaleString()}`}
//                   /> */}
//                 </div>
//               ) : (
//                 <p className="text-sm font-semibold text-slate-500">
//                   No sales records found.
//                 </p>
//               )}
//             </Section>
//           )}

//           {/* ✅ Agreement & E-Sign improved */}
//           <Section title="Agreement & E-Sign" subtitle="Links + status (shows signed doc link if available)">
//             <div className="space-y-3">
//               <div className="rounded-2xl border border-blue-100 bg-white p-4">
//                 <div className="flex items-center justify-between gap-2">
//                   <p className="text-sm font-semibold text-slate-900">Agreement</p>
//                   <Badge kind={stepStates[6] ? "green" : "gray"}>{stepStates[6] ? "Done" : "Pending"}</Badge>
//                 </div>
//                 <p className="mt-1 text-xs font-semibold text-slate-500">
//                   Currently inferred from <span className="font-semibold">is_verified ≥ 6</span>. Replace with your agreement flag if available.
//                 </p>

//                 {/* If backend provides agreement doc url later */}
//                 {normalized?.agreement_signed_doc_url ? (
//                   <div className="mt-3">
//                     <LinkBtn href={normalized.agreement_signed_doc_url}>View Signed Agreement</LinkBtn>
//                   </div>
//                 ) : null}
//               </div>

//               <div className="rounded-2xl border border-blue-100 bg-white p-4">
//                 <div className="flex items-center justify-between gap-2">
//                   <p className="text-sm font-semibold text-slate-900">E-Sign</p>
//                   <Badge kind={Number(normalized.esign_verified) === 1 ? "green" : "gray"}>
//                     {Number(normalized.esign_verified) === 1 ? "Verified" : "Not Verified"}
//                   </Badge>
//                 </div>

//                 <div className="mt-3 grid grid-cols-1 gap-3">
//                   <InfoCard icon={FileText} label="Document ID" value={normalized.document_id || esignData?.document_id} copy />
//                   <InfoCard icon={BadgeCheck} label="E-Sign Status (JSON)" value={esignData?.status || "—"} />
//                 </div>

//                 <div className="mt-4 flex flex-col gap-2">
//                   {/* ✅ If signed, show signed document link (if you have it) */}
//                   {Number(normalized.esign_verified) === 1 ? (
//                     signedDocUrl ? (
//                       <LinkBtn href={signedDocUrl}>View Signed Document</LinkBtn>
//                     ) : (
//                       <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm font-semibold text-blue-800">
//                         E-Sign verified ✅ but signed document URL not provided by API.
//                         <div className="mt-2 text-xs font-semibold text-blue-800/70">
//                           Add a field like <span className="font-semibold">esign_signed_doc_url</span> in payload to enable “View Signed Document”.
//                         </div>
//                       </div>
//                     )
//                   ) : (
//                     <>
//                       {/* Not signed yet: show signing link if available */}
//                       {signingLink ? (
//                         <LinkBtn href={signingLink}>Open Signing Link</LinkBtn>
//                       ) : (
//                         <div className="rounded-xl border border-blue-100 bg-slate-50 p-3 text-sm font-semibold text-slate-700">
//                           Signing link not available.
//                         </div>
//                       )}
//                     </>
//                   )}
//                 </div>

//                 <details className="mt-3 rounded-2xl border border-blue-100 bg-white p-4">
//                   <summary className="cursor-pointer text-sm font-semibold text-blue-700">View Raw E-Sign JSON</summary>
//                   <pre className="mt-3 max-h-80 overflow-auto rounded-xl bg-slate-900 p-4 text-xs text-slate-100">
// {JSON.stringify(esignData, null, 2)}
//                   </pre>
//                 </details>
//               </div>
//             </div>
//           </Section>

//         </div>
//       </div>
//         )}

//         {entityType === "company" && currentTab === 1 && (<AssignCompanyProducts companyId={id} />)}

//         {currentTab === 2 && (
//             <CompanyUsers
//               entityType={entityType}
//               entityId={id}
//             />
//           )}

//         {currentTab === 3 && (
//           <InvoicesView
//             fromUserView={true}
//             selectedUserId={id}
//             selectedUserRole={
//               entityType === "company"
//                 ? 2
//                 : entityType === "agent"
//                 ? 4
//                 : 5
//             }
//           />
//         )}

//         {currentTab === 4 && <CompanySales fromUserView={true}
//             selectedUserId={id}
//             selectedUserRole={
//               entityType === "company"
//                 ? 2
//                 : entityType === "agent"
//                 ? 4
//                 : 5
//             } />}

//         {currentTab === 5 && entityType === "retailer" && (
//           <RemarkChat
//             entityType={entityType}
//             entityId={id}
//           />
//         )}

//       <Outlet/>

//       {/* Layout */}
      
//       {/* <div className="mt-8 text-center text-xs font-semibold text-slate-500">
//         Blue/White theme • Responsive Tailwind • Optimized KYC & E-Sign UI
//       </div> */}

//       {showPaymentModal && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
//           <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
//             <h3 className="text-lg font-semibold text-slate-900">
//               Update Payment Method
//             </h3>

//             <p className="mt-1 text-sm font-semibold text-slate-500">
//               Select one option. This overrides user payment behavior.
//             </p>

//             <div className="mt-4 space-y-3">
//               {/* PAY NOW */}
//               <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 cursor-pointer hover:bg-slate-100">
//                 <div>
//                   <p className="text-sm font-semibold text-slate-900">Pay Now</p>
//                   <p className="text-xs font-semibold text-slate-500">
//                     Payment required immediately
//                   </p>
//                 </div>

//                 <input
//                   type="checkbox"
//                   checked={paymentSelection.paynow}
//                   disabled={updatingPayment}
//                   onChange={(e) =>
//                     setPaymentSelection((prev) => ({
//                       ...prev,
//                       paynow: e.target.checked,
//                     }))
//                   }
//                   className="h-4 w-4 accent-emerald-600"
//                 />
//               </label>

//               {/* PAY LATER */}
//               <label className="flex items-center justify-between rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 cursor-pointer hover:bg-slate-100">
//                 <div>
//                   <p className="text-sm font-semibold text-slate-900">Pay Later</p>
//                   <p className="text-xs font-semibold text-slate-500">
//                     Payment deferred to a later stage
//                   </p>
//                 </div>

//                 <input
//                   type="checkbox"
//                   checked={paymentSelection.paylater}
//                   disabled={updatingPayment}
//                   onChange={(e) =>
//                     setPaymentSelection((prev) => ({
//                       ...prev,
//                       paylater: e.target.checked,
//                     }))
//                   }
//                   className="h-4 w-4 accent-amber-600"
//                 />
//               </label>
//             </div>

//             <div className="mt-6 flex justify-end gap-2">
//               <button
//                 disabled={updatingPayment}
//                 onClick={() => {
//                   setShowPaymentModal(false);
//                   // setSelectedPayment(null);
//                 }}
//                 className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
//               >
//                 Cancel
//               </button>

//               <button
//                 disabled={
//                   updatingPayment ||
//                   (!paymentSelection.paynow && !paymentSelection.paylater)
//                 }
//                 onClick={() => handleAdminPaymentUpdate(paymentSelection)}
//                 className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50"
//               >
//                 Confirm
//               </button>

//             </div>
//           </div>
//         </div>
//       )}

//       {showLogoutConfirm && (
//         <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
//           <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
//             <h3 className="text-lg font-semibold text-slate-900">
//               {Number(normalized.is_logout) === 1
//                 ? "Remove Force Logout"
//                 : "Force Logout User"}
//             </h3>

//             <p className="mt-2 text-sm font-semibold text-slate-600">
//               {Number(normalized.is_logout) === 1
//                 ? "This will allow the user to log in again."
//                 : "This will immediately log the user out from all active sessions."}
//             </p>

//             <div className="mt-6 flex justify-end gap-2">
//               <button
//                 disabled={forcingLogout}
//                 onClick={() => setShowLogoutConfirm(false)}
//                 className="rounded-xl border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
//               >
//                 Cancel
//               </button>

//               <button
//                 disabled={forcingLogout}
//                 onClick={handleToggleLogout}
//                 className={`rounded-xl px-4 py-2 text-sm font-semibold text-white disabled:opacity-50
//                   ${
//                     Number(normalized.is_logout) === 1
//                       ? "bg-emerald-600 hover:bg-emerald-700"
//                       : "bg-rose-600 hover:bg-rose-700"
//                   }
//                 `}
//               >
//                 {forcingLogout
//                   ? "Please wait…"
//                   : Number(normalized.is_logout) === 1
//                   ? "Remove Logout"
//                   : "Confirm Logout"}
//               </button>
//             </div>
//           </div>
//         </div>
//       )}

//     </div>
//   );
// }


// import React, { useMemo } from "react";
// import { useLocation, useNavigate, useParams } from "react-router-dom";
// import {
//   ArrowLeft,
//   BadgeCheck,
//   Building2,
//   Calendar,
//   CheckCircle2,
//   ClipboardCopy,
//   CreditCard,
//   FileCheck2,
//   FileText,
//   Hash,
//   Landmark,
//   Link as LinkIcon,
//   Mail,
//   MapPin,
//   Phone,
//   ShieldCheck,
//   User,
//   XCircle,
// } from "lucide-react";

// const steps = [
//   { id: 1, name: "Verify Email & Phone" },
//   { id: 2, name: "Payment" },
//   { id: 3, name: "PAN Verification" },
//   { id: 4, name: "GST Details" },
//   { id: 5, name: "Bank Details" },
//   { id: 6, name: "Agreement" },
//   { id: 7, name: "E-Sign Verify" },
// ];

// function safeJsonParse(value) {
//   try {
//     if (!value) return null;
//     if (typeof value === "object") return value;
//     return JSON.parse(value);
//   } catch {
//     return null;
//   }
// }

// function Badge({ tone = "blue", children }) {
//   const tones = {
//     blue: "bg-blue-50 text-blue-700 border-blue-200",
//     green: "bg-emerald-50 text-emerald-700 border-emerald-200",
//     red: "bg-rose-50 text-rose-700 border-rose-200",
//     gray: "bg-slate-50 text-slate-700 border-slate-200",
//     amber: "bg-amber-50 text-amber-700 border-amber-200",
//   };
//   return (
//     <span className={`inline-flex items-center rounded-full border px-2.5 py-1 text-xs font-semibold ${tones[tone]}`}>
//       {children}
//     </span>
//   );
// }

// function StatRow({ icon: Icon, label, value, copy }) {
//   const show = value !== null && value !== undefined && value !== "" ? value : "—";
//   return (
//     <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-white p-3">
//       <div className="mt-0.5 rounded-lg bg-blue-50 p-2 text-blue-700">
//         <Icon className="h-4 w-4" />
//       </div>
//       <div className="min-w-0 flex-1">
//         <p className="text-xs font-semibold text-slate-500">{label}</p>
//         <p className="truncate text-sm font-semibold text-slate-800">{String(show)}</p>
//       </div>
//       {copy && show !== "—" && (
//         <button
//           type="button"
//           onClick={() => navigator.clipboard.writeText(String(show))}
//           className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100"
//           title="Copy"
//         >
//           <ClipboardCopy className="h-4 w-4" />
//           Copy
//         </button>
//       )}
//     </div>
//   );
// }

// function Section({ title, right, children }) {
//   return (
//     <div className="rounded-2xl border border-blue-100 bg-white p-5 shadow-sm">
//       <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
//         <h2 className="text-base font-bold text-slate-900">{title}</h2>
//         {right}
//       </div>
//       {children}
//     </div>
//   );
// }

// // Derive step status from your payload
// function getStepStates(c) {
//   const emailPhoneDone = Boolean(normalized?.is_mail_verified) && Boolean(normalized?.is_wa_verified);
//   const paymentDone = Number(normalized?.is_payment_success) === 1;
//   const panDone = Number(normalized?.pan_verified) === 1;
//   const gstDone = Boolean(normalized?.gst);
//   const bankDone = Boolean(normalized?.account_no) && Boolean(normalized?.bank_name) && Boolean(normalized?.branch_name);
//   // Agreement: if your backend has a flag, swap it here; for now infer from is_verified stage >= 6
//   const agreementDone = Number(normalized?.is_verified) >= 6;
//   const esignDone = Number(normalized?.esign_verified) === 1;

//   return {
//     1: emailPhoneDone,
//     2: paymentDone,
//     3: panDone,
//     4: gstDone,
//     5: bankDone,
//     6: agreementDone,
//     7: esignDone,
//   };
// }

// function Stepper({ currentStage, states }) {
//   return (
//     <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
//       {steps.map((s) => {
//         const done = Boolean(states?.[s.id]);
//         const active = Number(currentStage) === s.id;
//         return (
//           <div
//             key={s.id}
//             className={[
//               "flex items-center justify-between rounded-2xl border p-4",
//               done ? "border-emerald-200 bg-emerald-50" : active ? "border-blue-200 bg-blue-50" : "border-blue-100 bg-white",
//             ].join(" ")}
//           >
//             <div className="flex items-center gap-3">
//               <div
//                 className={[
//                   "flex h-9 w-9 items-center justify-center rounded-xl border",
//                   done
//                     ? "border-emerald-200 bg-white text-emerald-700"
//                     : active
//                       ? "border-blue-200 bg-white text-blue-700"
//                       : "border-blue-100 bg-blue-50 text-blue-700",
//                 ].join(" ")}
//               >
//                 {done ? <CheckCircle2 className="h-5 w-5" /> : <span className="text-sm font-semibold">{s.id}</span>}
//               </div>
//               <div>
//                 <p className="text-sm font-bold text-slate-900">{s.name}</p>
//                 <p className="text-xs font-semibold text-slate-500">
//                   {done ? "Completed" : active ? "In progress" : "Pending"}
//                 </p>
//               </div>
//             </div>

//             {done ? <Badge tone="green">Done</Badge> : active ? <Badge tone="blue">Active</Badge> : <Badge tone="gray">Pending</Badge>}
//           </div>
//         );
//       })}
//     </div>
//   );
// }

// export default function CompanyDetails() {
//   const { id } = useParams();
//   const navigate = useNavigate();
//   const location = useLocation();

//   const c = location?.state?.c; // from navigate(`/company/${normalized.id}`, { state: { c } })

//   const panData = useMemo(() => safeJsonParse(normalized?.pan_json), [normalized?.pan_json]);
//   const esignData = useMemo(() => safeJsonParse(normalized?.esign_json), [normalized?.esign_json]);

//   const stepStates = useMemo(() => getStepStates(c), [c]);

//   // If user refreshes, state can be lost. You can fetch by :id here if needed.
//   if (!c) {
//     return (
//       <div className="mx-auto max-w-5xl p-4 md:p-8">
//         <div className="rounded-2xl border border-blue-100 bg-white p-6 text-center shadow-sm">
//           <p className="text-base font-bold text-slate-900">No company data found</p>
//           <p className="mt-1 text-sm text-slate-600">
//             You may have refreshed the page. Navigate from the company list again or load data by ID: <span className="font-semibold">{id}</span>.
//           </p>
//           <button
//             type="button"
//             onClick={() => navigate(-1)}
//             className="mt-4 inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
//           >
//             <ArrowLeft className="h-4 w-4" />
//             Go Back
//           </button>
//         </div>
//       </div>
//     );
//   }

//   const created = normalized?.created_at ? new Date(normalized.created_at).toLocaleString() : "—";
//   const updated = normalized?.updated_at ? new Date(normalized.updated_at).toLocaleString() : "—";

//   const mailOk = Boolean(normalized?.is_mail_verified);
//   const waOk = Boolean(normalized?.is_wa_verified);

//   return (
//     <div className="mx-auto max-w-6xl p-4 md:p-8">
//       {/* Header */}
//       <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
//         <div className="min-w-0">
//           <div className="flex flex-wrap items-center gap-2">
//             <h1 className="truncate text-xl font-semibold text-slate-900 md:text-2xl">
//               {normalized.business_name || "Company Details"}
//             </h1>
//             <Badge tone="blue">ID: {normalized.id}</Badge>
//             <Badge tone={Number(normalized.is_payment_success) === 1 ? "green" : "amber"}>
//               {Number(normalized.is_payment_success) === 1 ? "Payment Success" : "Payment Pending"}
//             </Badge>
//             <Badge tone={Number(normalized.esign_verified) === 1 ? "green" : "gray"}>
//               {Number(normalized.esign_verified) === 1 ? "E-Sign Verified" : "E-Sign Not Verified"}
//             </Badge>
//           </div>

//           <p className="mt-2 text-sm font-semibold text-slate-600">
//             Trade Name: <span className="font-bold text-slate-900">{normalized.trade_name || "—"}</span>
//           </p>
//         </div>

//         <div className="flex flex-wrap gap-2">
//           <button
//             type="button"
//             onClick={() => navigate(-1)}
//             className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-50"
//           >
//             <ArrowLeft className="h-4 w-4" />
//             Back
//           </button>
//           <button
//             type="button"
//             onClick={() => navigator.clipboard.writeText(JSON.stringify(c, null, 2))}
//             className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
//           >
//             <ClipboardCopy className="h-4 w-4" />
//             Copy Payload
//           </button>
//         </div>
//       </div>

//       {/* Main Grid */}
//       <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
//         {/* Left Column */}
//         <div className="space-y-6 lg:col-span-2">
//           <Section
//             title="Onboarding Progress"
//             right={
//               <div className="flex flex-wrap items-center gap-2">
//                 <Badge tone="blue">
//                   Stage: <span className="ml-1 font-semibold">{normalized.is_verified ?? "—"}</span>
//                 </Badge>
//                 <Badge tone={mailOk ? "green" : "gray"}>
//                   <Mail className="mr-1 h-3.5 w-3.5" /> Email {mailOk ? "Verified" : "Pending"}
//                 </Badge>
//                 <Badge tone={waOk ? "green" : "gray"}>
//                   <Phone className="mr-1 h-3.5 w-3.5" /> Phone/WA {waOk ? "Verified" : "Pending"}
//                 </Badge>
//               </div>
//             }
//           >
//             <Stepper currentStage={normalized.is_verified} states={stepStates} />

//             {esignData?.signing_link && Number(normalized.esign_verified) !== 1 && (
//               <div className="mt-4 rounded-2xl border border-blue-100 bg-blue-50 p-4">
//                 <div className="flex flex-wrap items-center justify-between gap-3">
//                   <div className="flex items-center gap-2">
//                     <LinkIcon className="h-4 w-4 text-blue-700" />
//                     <p className="text-sm font-bold text-blue-800">E-Sign link available</p>
//                   </div>
//                   <a
//                     href={esignData.signing_link}
//                     target="_blank"
//                     rel="noreferrer"
//                     className="inline-flex items-center gap-2 rounded-xl border border-blue-200 bg-white px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
//                   >
//                     Open Signing Link
//                     <ArrowLeft className="h-4 w-4 rotate-180" />
//                   </a>
//                 </div>
//                 <p className="mt-2 text-xs font-semibold text-blue-700/80">
//                   Note: Link opens in a new tab. If it expires, generate a new signing link from backend.
//                 </p>
//               </div>
//             )}
//           </Section>

//           <Section title="Company & Contact">
//             <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
//               <StatRow icon={Building2} label="Business Name" value={normalized.business_name} copy />
//               <StatRow icon={User} label="Contact Person" value={normalized.contact_person} copy />
//               <StatRow icon={Phone} label="Phone" value={normalized.contact_phone} copy />
//               <StatRow icon={Mail} label="Email" value={normalized.contact_email} copy />
//             </div>
//           </Section>

//           <Section title="Address">
//             <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
//               <StatRow icon={MapPin} label="Address Line 1" value={normalized.address_line1} />
//               <StatRow icon={MapPin} label="Address Line 2" value={normalized.address_line2} />
//               <StatRow icon={MapPin} label="City" value={normalized.city} />
//               <StatRow icon={MapPin} label="District" value={normalized.district} />
//               <StatRow icon={MapPin} label="State" value={normalized.state} />
//               <StatRow icon={Hash} label="Pincode" value={normalized.pincode} copy />
//             </div>
//           </Section>

//           <Section title="KYC: PAN & GST">
//             <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
//               <StatRow icon={ShieldCheck} label="PAN" value={normalized.pan} copy />
//               <div className="rounded-xl border border-blue-100 bg-white p-3">
//                 <p className="text-xs font-semibold text-slate-500">PAN Verified</p>
//                 <div className="mt-1 flex items-center gap-2">
//                   {Number(normalized.pan_verified) === 1 ? (
//                     <>
//                       <CheckCircle2 className="h-4 w-4 text-emerald-600" />
//                       <p className="text-sm font-bold text-slate-900">Yes</p>
//                       <Badge tone="green">VALID</Badge>
//                     </>
//                   ) : (
//                     <>
//                       <XCircle className="h-4 w-4 text-rose-600" />
//                       <p className="text-sm font-bold text-slate-900">No</p>
//                       <Badge tone="amber">Pending</Badge>
//                     </>
//                   )}
//                 </div>
//               </div>

//               <StatRow icon={FileText} label="GST" value={normalized.gst || "Not Provided"} copy={Boolean(normalized.gst)} />

//               {panData && (
//                 <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 md:col-span-2">
//                   <div className="flex flex-wrap items-center justify-between gap-2">
//                     <p className="text-sm font-semibold text-blue-900">PAN Verification Details</p>
//                     <Badge tone="blue">{panData.status || "—"}</Badge>
//                   </div>

//                   <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
//                     <StatRow icon={BadgeCheck} label="Registered Name" value={panData.registered_name} />
//                     <StatRow icon={BadgeCheck} label="PAN Card Name" value={panData.name_pan_card} />
//                     <StatRow icon={Calendar} label="Date of Birth" value={panData.date_of_birth} />
//                     <StatRow icon={Phone} label="Mobile (masked)" value={panData.mobile_number} />
//                   </div>

//                   {panData?.address?.full_address && (
//                     <div className="mt-3 rounded-xl border border-blue-100 bg-white p-3">
//                       <p className="text-xs font-semibold text-slate-500">PAN Address</p>
//                       <p className="mt-1 text-sm font-semibold text-slate-800">{panData.address.full_address}</p>
//                     </div>
//                   )}
//                 </div>
//               )}
//             </div>
//           </Section>
//         </div>

//         {/* Right Column */}
//         <div className="space-y-6">
//           <Section title="Bank Details" right={<Badge tone={stepStates[5] ? "green" : "amber"}>{stepStates[5] ? "Complete" : "Pending"}</Badge>}>
//             <div className="grid grid-cols-1 gap-3">
//               <StatRow icon={Landmark} label="Bank Name" value={normalized.bank_name} />
//               <StatRow icon={Landmark} label="Branch Name" value={normalized.branch_name} />
//               <StatRow icon={Hash} label="Account No" value={normalized.account_no} copy />
//               <StatRow icon={Hash} label="IFSC Code" value={normalized.ifsc_code || "Not Provided"} copy={Boolean(normalized.ifsc_code)} />
//               <StatRow icon={FileCheck2} label="Account Type" value={normalized.account_type || "—"} />
//             </div>
//           </Section>

//           <Section title="Payment" right={<CreditCard className="h-5 w-5 text-blue-700" />}>
//             <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
//               <div className="flex items-center justify-between">
//                 <p className="text-sm font-semibold text-blue-900">Payment Status</p>
//                 {Number(normalized.is_payment_success) === 1 ? <Badge tone="green">Success</Badge> : <Badge tone="amber">Pending</Badge>}
//               </div>
//               <p className="mt-2 text-xs font-semibold text-blue-700/80">
//                 Field: <span className="font-bold">is_payment_success</span> = {String(normalized.is_payment_success)}
//               </p>
//             </div>
//           </Section>

//           <Section title="Agreement & E-Sign">
//             <div className="space-y-3">
//               <div className="rounded-xl border border-blue-100 bg-white p-4">
//                 <div className="flex items-center justify-between">
//                   <p className="text-sm font-bold text-slate-900">Agreement Stage</p>
//                   <Badge tone={stepStates[6] ? "green" : "gray"}>{stepStates[6] ? "Done" : "Pending"}</Badge>
//                 </div>
//                 <p className="mt-1 text-xs font-semibold text-slate-500">
//                   Inferred from is_verified ≥ 6 (change this logic if you have an explicit agreement flag).
//                 </p>
//               </div>

//               <div className="rounded-xl border border-blue-100 bg-white p-4">
//                 <div className="flex items-center justify-between">
//                   <p className="text-sm font-bold text-slate-900">E-Sign</p>
//                   <Badge tone={Number(normalized.esign_verified) === 1 ? "green" : "gray"}>
//                     {Number(normalized.esign_verified) === 1 ? "Verified" : "Not Verified"}
//                   </Badge>
//                 </div>

//                 <div className="mt-3 grid grid-cols-1 gap-3">
//                   <StatRow icon={FileText} label="Document ID" value={normalized.document_id || esignData?.document_id} copy />
//                   <StatRow icon={BadgeCheck} label="E-Sign Status" value={esignData?.status || "—"} />
//                 </div>

//                 {esignData?.signing_link && (
//                   <a
//                     href={esignData.signing_link}
//                     target="_blank"
//                     rel="noreferrer"
//                     className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-4 py-2 text-sm font-semibold text-blue-700 hover:bg-blue-100"
//                   >
//                     <LinkIcon className="h-4 w-4" />
//                     Open Signing Link
//                   </a>
//                 )}
//               </div>
//             </div>
//           </Section>

//           <Section title="Zoho Organisation">
//             <div className="grid grid-cols-1 gap-3">
//               <StatRow icon={Building2} label="Zoho ID" value={normalized.zoho_id || "—"} copy={Boolean(normalized.zoho_id)} />
//               <StatRow icon={Building2} label="Zoho Org ID" value={normalized.zoho_org_id || "Not Connected"} copy={Boolean(normalized.zoho_org_id)} />
//               <StatRow icon={Hash} label="Company Code" value={normalized.company_code || "—"} copy={Boolean(normalized.company_code)} />
//             </div>
//           </Section>

//           <Section title="Audit">
//             <div className="grid grid-cols-1 gap-3">
//               <StatRow icon={Calendar} label="Created At" value={created} />
//               <StatRow icon={Calendar} label="Updated At" value={updated} />
//               <div className="rounded-xl border border-blue-100 bg-white p-4">
//                 <p className="text-xs font-semibold text-slate-500">Status</p>
//                 <div className="mt-1 flex items-center gap-2">
//                   {Number(normalized.status) === 1 ? (
//                     <>
//                       <CheckCircle2 className="h-4 w-4 text-emerald-600" />
//                       <p className="text-sm font-bold text-slate-900">Active</p>
//                     </>
//                   ) : (
//                     <>
//                       <XCircle className="h-4 w-4 text-rose-600" />
//                       <p className="text-sm font-bold text-slate-900">Inactive</p>a
//                     </>
//                   )}
//                 </div>
//               </div>
//             </div>
//           </Section>
//         </div>
//       </div>

//       <div className="mt-8 text-center text-xs font-semibold text-slate-500">
//         Blue/White theme • Responsive • Tailwind UI
//       </div>
//     </div>
//   );
// }
